import type { Question } from '../types';

export { type QuestionTopic, type QuestionDifficulty, type QuestionChoice, type Question, QUESTION_TOPICS } from '../types';

export const questionBank: Question[] = [
  {
    id: 'q001',
    topic: 'tceq-regulations',
    difficulty: 'basic',
    question: 'According to TCEQ Chapter 76, what is the minimum radial annular space required between well casing and borehole wall?',
    choices: [
      { id: 'a', text: '0.5 inches' },
      { id: 'b', text: '1.5 inches' },
      { id: 'c', text: '2.0 inches' },
      { id: 'd', text: '3.0 inches' },
    ],
    correctAnswer: 'b',
    explanation: 'TCEQ Chapter 76 specifies a minimum radial annular space of 1.5 inches between the well casing and the borehole wall to ensure proper grouting and seal integrity.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q002',
    topic: 'tceq-regulations',
    difficulty: 'basic',
    question: 'What is the maximum bentonite percentage allowed when grouting with neat cement per TCEQ 16 TAC §76.702?',
    choices: [
      { id: 'a', text: '2%' },
      { id: 'b', text: '4%' },
      { id: 'c', text: '6%' },
      { id: 'd', text: '8%' },
    ],
    correctAnswer: 'c',
    explanation: 'TCEQ regulations specify that neat cement grout may contain a maximum of 6% bentonite by weight to maintain structural integrity and hydraulic conductivity requirements.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q003',
    topic: 'tceq-regulations',
    difficulty: 'intermediate',
    question: 'Under TCEQ Chapter 76, which type of casing material is prohibited for use in high-yield water wells?',
    choices: [
      { id: 'a', text: 'Steel casing' },
      { id: 'b', text: 'PVC casing' },
      { id: 'c', text: 'Stainless steel casing' },
      { id: 'd', text: 'Vinyl casing' },
    ],
    correctAnswer: 'd',
    explanation: 'TCEQ Chapter 76 prohibits vinyl and other non-rigid materials as casing in production wells. Steel, stainless steel, and properly rated PVC are acceptable materials.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q004',
    topic: 'tceq-regulations',
    difficulty: 'intermediate',
    question: 'What is the primary purpose of a sanitary seal per TCEQ well construction standards?',
    choices: [
      { id: 'a', text: 'Prevent sediment from entering the well' },
      { id: 'b', text: 'Prevent surface contamination from entering the well' },
      { id: 'c', text: 'Reduce pumping noise' },
      { id: 'd', text: 'Prevent water loss through the casing' },
    ],
    correctAnswer: 'b',
    explanation: 'A sanitary seal is installed at the surface to prevent surface contamination, including bacteria, chemicals, and other pollutants, from entering the well through the annular space.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q005',
    topic: 'tceq-regulations',
    difficulty: 'basic',
    question: 'According to TCEQ 16 TAC §76.1004, how are water wells classified for plugging and abandonment purposes?',
    choices: [
      { id: 'a', text: 'By depth only' },
      { id: 'b', text: 'By yield only' },
      { id: 'c', text: 'By aquifer formation and use' },
      { id: 'd', text: 'By casing material only' },
    ],
    correctAnswer: 'c',
    explanation: 'TCEQ classifies wells by aquifer formation and intended use to determine appropriate plugging and abandonment procedures, ensuring protection of all groundwater zones.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q006',
    topic: 'tceq-regulations',
    difficulty: 'intermediate',
    question: 'What information must be included on a well completion report submitted to TCEQ?',
    choices: [
      { id: 'a', text: 'Only the well depth and yield' },
      { id: 'b', text: 'Driller license, well location, construction details, and static water level' },
      { id: 'c', text: 'Only the owner contact information' },
      { id: 'd', text: 'Only the drilling duration' },
    ],
    correctAnswer: 'b',
    explanation: 'TCEQ well completion reports must include the driller\'s license number, well location coordinates, detailed construction specifications, water levels, and pump information.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q007',
    topic: 'tceq-regulations',
    difficulty: 'basic',
    question: 'Which TCEQ rule governs the maximum pitless adapter depth below grade?',
    choices: [
      { id: 'a', text: 'TCEQ Chapter 76' },
      { id: 'b', text: 'TCEQ Chapter 290' },
      { id: 'c', text: 'TCEQ Chapter 49' },
      { id: 'd', text: 'TCEQ Chapter 115' },
    ],
    correctAnswer: 'a',
    explanation: 'TCEQ Chapter 76 contains well construction standards including pitless adapter requirements and installation depth specifications.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q008',
    topic: 'tceq-regulations',
    difficulty: 'intermediate',
    question: 'According to TCEQ Chapter 290, what is the maximum contaminant level (MCL) for total coliform bacteria in drinking water?',
    choices: [
      { id: 'a', text: 'No more than 5% of samples per month' },
      { id: 'b', text: 'No more than 10% of samples per month' },
      { id: 'c', text: 'No detectable coliform in any sample' },
      { id: 'd', text: 'Less than 1 CFU/mL' },
    ],
    correctAnswer: 'a',
    explanation: 'TCEQ Chapter 290 (which adopts EPA standards) requires no more than 5% of samples to test positive for total coliform bacteria in a monthly sampling period.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q009',
    topic: 'tceq-regulations',
    difficulty: 'advanced',
    question: 'What is the procedure for grouting a well with tremie pipe according to TCEQ standards?',
    choices: [
      { id: 'a', text: 'Pour grout from the surface without submerged discharge' },
      { id: 'b', text: 'Use tremie pipe with submerged discharge, gradually raising the pipe as grout rises in the annulus' },
      { id: 'c', text: 'Use an open bucket to pour grout directly into the annulus' },
      { id: 'd', text: 'Pump grout under high pressure into the annular space' },
    ],
    correctAnswer: 'b',
    explanation: 'TCEQ requires tremie pipe grouting with the discharge end submerged in grout, gradually withdrawing the pipe as the annulus fills to prevent segregation and ensure continuous grout placement.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q010',
    topic: 'tceq-regulations',
    difficulty: 'basic',
    question: 'How does TCEQ Chapter 76 define a high-yield water well?',
    choices: [
      { id: 'a', text: 'Any well producing more than 10 gpm' },
      { id: 'b', text: 'Any well producing more than 50 gpm' },
      { id: 'c', text: 'Any well producing more than 100 gpm' },
      { id: 'd', text: 'Any well producing more than 200 gpm' },
    ],
    correctAnswer: 'c',
    explanation: 'TCEQ Chapter 76 defines a high-yield well as one capable of producing more than 100 gpm, which triggers enhanced construction requirements.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q011',
    topic: 'tceq-regulations',
    difficulty: 'intermediate',
    question: 'What is the required clearance between the top of a well and any potential contamination source per TCEQ?',
    choices: [
      { id: 'a', text: '25 feet' },
      { id: 'b', text: '50 feet' },
      { id: 'c', text: '100 feet' },
      { id: 'd', text: '150 feet' },
    ],
    correctAnswer: 'b',
    explanation: 'TCEQ Chapter 76 requires a minimum of 50 feet horizontal separation from potential contamination sources such as septic systems or fuel storage.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q012',
    topic: 'tceq-regulations',
    difficulty: 'advanced',
    question: 'According to TCEQ 16 TAC §76.702, what is the minimum thickness of the grout seal in a well annulus?',
    choices: [
      { id: 'a', text: '1 inch' },
      { id: 'b', text: '2 inches' },
      { id: 'c', text: '5 inches' },
      { id: 'd', text: '10 inches' },
    ],
    correctAnswer: 'c',
    explanation: 'TCEQ regulations require a minimum 5-inch thick grout seal in the annular space to provide adequate protection and structural integrity.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q013',
    topic: 'tceq-regulations',
    difficulty: 'basic',
    question: 'Which state agency regulates water well drilling in Texas?',
    choices: [
      { id: 'a', text: 'Texas Parks and Wildlife Department' },
      { id: 'b', text: 'Texas Commission on Environmental Quality (TCEQ)' },
      { id: 'c', text: 'Texas Department of Transportation' },
      { id: 'd', text: 'Texas Water Development Board only' },
    ],
    correctAnswer: 'b',
    explanation: 'The TCEQ (Texas Commission on Environmental Quality) is the primary state agency responsible for regulating water well drilling under Chapter 76.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q014',
    topic: 'tceq-regulations',
    difficulty: 'intermediate',
    question: 'What does TCEQ Chapter 76 specify regarding well screen slot size selection?',
    choices: [
      { id: 'a', text: 'Always use 0.010 inch slots' },
      { id: 'b', text: 'Select based on aquifer formation grain size to prevent sand intrusion' },
      { id: 'c', text: 'Use the largest available slot size' },
      { id: 'd', text: 'Slot size is not regulated' },
    ],
    correctAnswer: 'b',
    explanation: 'TCEQ regulations require that well screen slot size be selected based on the formation grain size analysis, typically sizing the screen to retain 80-90% of the formation material.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q015',
    topic: 'tceq-regulations',
    difficulty: 'advanced',
    question: 'According to TCEQ standards, what is the maximum depth at which a pitless adapter may be installed?',
    choices: [
      { id: 'a', text: '2 feet below grade' },
      { id: 'b', text: '4 feet below grade' },
      { id: 'c', text: '6 feet below grade' },
      { id: 'd', text: 'No specified limit' },
    ],
    correctAnswer: 'b',
    explanation: 'TCEQ Chapter 76 specifies that pitless adapters must not be installed more than 4 feet below ground level to prevent frost damage and ensure proper maintenance access.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q016',
    topic: 'tdlr-licensing',
    difficulty: 'basic',
    question: 'How many years of documented experience are required to apply for a water well driller license in Texas?',
    choices: [
      { id: 'a', text: '1 year' },
      { id: 'b', text: '2 years' },
      { id: 'c', text: '3 years' },
      { id: 'd', text: '5 years' },
    ],
    correctAnswer: 'b',
    explanation: 'The Texas Department of Licensing and Regulation (TDLR) requires 2 years of documented water well drilling experience before an applicant is eligible to take the driller license examination.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q017',
    topic: 'tdlr-licensing',
    difficulty: 'basic',
    question: 'What is the minimum passing score on the TDLR water well driller license exam?',
    choices: [
      { id: 'a', text: '60%' },
      { id: 'b', text: '65%' },
      { id: 'c', text: '70%' },
      { id: 'd', text: '75%' },
    ],
    correctAnswer: 'c',
    explanation: 'TDLR requires a minimum score of 70% on the water well driller license examination to demonstrate competency in well drilling practices.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q018',
    topic: 'tdlr-licensing',
    difficulty: 'basic',
    question: 'How long is a TDLR water well driller license valid?',
    choices: [
      { id: 'a', text: '1 year' },
      { id: 'b', text: '2 years' },
      { id: 'c', text: '3 years' },
      { id: 'd', text: '5 years' },
    ],
    correctAnswer: 'b',
    explanation: 'TDLR water well driller licenses are valid for 2 years from the date of issuance, after which the licensee must renew the license.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q019',
    topic: 'tdlr-licensing',
    difficulty: 'intermediate',
    question: 'What continuing education (CE) requirement must a water well driller meet for license renewal in Texas?',
    choices: [
      { id: 'a', text: '4 hours total with no specific requirements' },
      { id: 'b', text: '8 hours total with 1 hour dedicated to TDLR rules and regulations' },
      { id: 'c', text: '12 hours total with 2 hours on rules' },
      { id: 'd', text: '16 hours total with 4 hours on rules' },
    ],
    correctAnswer: 'b',
    explanation: 'TDLR requires 8 hours of continuing education for driller license renewal, with at least 1 hour specifically covering TDLR rules and regulations.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q020',
    topic: 'tdlr-licensing',
    difficulty: 'intermediate',
    question: 'Can a water well driller license be transferred to another individual?',
    choices: [
      { id: 'a', text: 'Yes, with TDLR approval' },
      { id: 'b', text: 'Yes, without restriction' },
      { id: 'c', text: 'No, licenses are non-transferable' },
      { id: 'd', text: 'Only to family members' },
    ],
    correctAnswer: 'c',
    explanation: 'TDLR water well driller licenses are non-transferable. Each individual must obtain their own license through examination and experience requirements.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q021',
    topic: 'tdlr-licensing',
    difficulty: 'basic',
    question: 'What license type does a professional who installs water well pumps need in Texas?',
    choices: [
      { id: 'a', text: 'Water well driller license' },
      { id: 'b', text: 'Water well pump installer license' },
      { id: 'c', text: 'Electrical contractor license' },
      { id: 'd', text: 'Plumbing license' },
    ],
    correctAnswer: 'b',
    explanation: 'A person who installs water well pumps must hold a TDLR water well pump installer license, which has similar experience and testing requirements to the driller license.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q022',
    topic: 'tdlr-licensing',
    difficulty: 'intermediate',
    question: 'Under what circumstances can an unlicensed person perform water well drilling in Texas?',
    choices: [
      { id: 'a', text: 'Never - all drilling requires a license' },
      { id: 'b', text: 'When supervised by a licensed driller' },
      { id: 'c', text: 'For domestic wells on residential property' },
      { id: 'd', text: 'For monitoring wells' },
    ],
    correctAnswer: 'b',
    explanation: 'An unlicensed person may drill water wells only when working under the direct supervision of a licensed water well driller who is responsible for the work quality.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q023',
    topic: 'tdlr-licensing',
    difficulty: 'advanced',
    question: 'What documentation must a TDLR applicant provide to verify the required 2 years of drilling experience?',
    choices: [
      { id: 'a', text: 'A personal affidavit only' },
      { id: 'b', text: 'Verification of employment and detailed work logs' },
      { id: 'c', text: 'References from well owners' },
      { id: 'd', text: 'Copies of well permits' },
    ],
    correctAnswer: 'b',
    explanation: 'TDLR requires documented verification of employment history and detailed work logs showing the applicant performed water well drilling duties for at least 2 years.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q024',
    topic: 'tdlr-licensing',
    difficulty: 'intermediate',
    question: 'How often must a water well driller renew their TDLR license?',
    choices: [
      { id: 'a', text: 'Annually' },
      { id: 'b', text: 'Every 2 years' },
      { id: 'c', text: 'Every 3 years' },
      { id: 'd', text: 'Every 5 years' },
    ],
    correctAnswer: 'b',
    explanation: 'TDLR water well driller licenses must be renewed every 2 years, requiring completion of the required continuing education hours.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q025',
    topic: 'tdlr-licensing',
    difficulty: 'basic',
    question: 'Which agency issues water well driller and pump installer licenses in Texas?',
    choices: [
      { id: 'a', text: 'TCEQ' },
      { id: 'b', text: 'Texas Water Development Board' },
      { id: 'c', text: 'Texas Department of Licensing and Regulation (TDLR)' },
      { id: 'd', text: 'Local county health departments' },
    ],
    correctAnswer: 'c',
    explanation: 'The Texas Department of Licensing and Regulation (TDLR) is responsible for issuing and regulating water well driller and pump installer licenses.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q026',
    topic: 'groundwater-districts',
    difficulty: 'basic',
    question: 'Which counties are served by the North Texas Groundwater Conservation District (GCD)?',
    choices: [
      { id: 'a', text: 'Collin, Cooke, and Denton' },
      { id: 'b', text: 'Tarrant and Dallas' },
      { id: 'c', text: 'Ellis and Parker' },
      { id: 'd', text: 'Montague, Wise, and Hood' },
    ],
    correctAnswer: 'a',
    explanation: 'The North Texas GCD has jurisdiction over groundwater in Collin, Cooke, and Denton counties in North Texas.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q027',
    topic: 'groundwater-districts',
    difficulty: 'intermediate',
    question: 'What is the exemption threshold for the North Texas Groundwater Conservation District?',
    choices: [
      { id: 'a', text: 'Wells producing less than 10 gpm' },
      { id: 'b', text: 'Wells producing less than 17.36 gpm' },
      { id: 'c', text: 'Wells producing less than 25 gpm' },
      { id: 'd', text: 'All domestic wells' },
    ],
    correctAnswer: 'b',
    explanation: 'The North Texas GCD exempts wells from registration if they produce less than 17.36 gpm (or 25 acre-feet per year), typically for domestic use.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q028',
    topic: 'groundwater-districts',
    difficulty: 'basic',
    question: 'What is the required setback distance for wells in the North Texas GCD from property lines?',
    choices: [
      { id: 'a', text: '25 feet' },
      { id: 'b', text: '50 feet' },
      { id: 'c', text: '100 feet' },
      { id: 'd', text: '150 feet' },
    ],
    correctAnswer: 'b',
    explanation: 'The North Texas GCD requires a minimum setback of 50 feet from property lines for water well locations.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q029',
    topic: 'groundwater-districts',
    difficulty: 'basic',
    question: 'Which counties fall under the jurisdiction of the Upper Trinity Groundwater Conservation District?',
    choices: [
      { id: 'a', text: 'Collin, Cooke, and Denton' },
      { id: 'b', text: 'Montague, Wise, Parker, and Hood' },
      { id: 'c', text: 'Tarrant and Ellis' },
      { id: 'd', text: 'Johnson and Somervell' },
    ],
    correctAnswer: 'b',
    explanation: 'The Upper Trinity GCD has jurisdiction over groundwater in Montague, Wise, Parker, and Hood counties.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q030',
    topic: 'groundwater-districts',
    difficulty: 'intermediate',
    question: 'What is the minimum well spacing requirement in the Upper Trinity GCD?',
    choices: [
      { id: 'a', text: '50 feet' },
      { id: 'b', text: '100 feet' },
      { id: 'c', text: '150 feet' },
      { id: 'd', text: '300 feet' },
    ],
    correctAnswer: 'c',
    explanation: 'The Upper Trinity GCD requires a minimum spacing of 150 feet between production wells to prevent interference.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q031',
    topic: 'groundwater-districts',
    difficulty: 'intermediate',
    question: 'When must a well be registered with a Groundwater Conservation District?',
    choices: [
      { id: 'a', text: 'After drilling is complete' },
      { id: 'b', text: 'Before drilling begins' },
      { id: 'c', text: 'Only if the well produces more than 100 gpm' },
      { id: 'd', text: 'Registration is optional' },
    ],
    correctAnswer: 'b',
    explanation: 'Most GCDs require well registration before drilling begins to ensure compliance with spacing, setback, and other regulations.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q032',
    topic: 'groundwater-districts',
    difficulty: 'basic',
    question: 'What is the standard completion timeline for a registered well in a Groundwater Conservation District?',
    choices: [
      { id: 'a', text: '60 days' },
      { id: 'b', text: '120 days' },
      { id: 'c', text: '240 days' },
      { id: 'd', text: '1 year' },
    ],
    correctAnswer: 'c',
    explanation: 'Most GCDs allow 240 days for well completion from the date of registration before the permit expires.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q033',
    topic: 'groundwater-districts',
    difficulty: 'intermediate',
    question: 'What is required when a well is designed to produce 200 gpm or more in a Groundwater Conservation District?',
    choices: [
      { id: 'a', text: 'Only a well permit' },
      { id: 'b', text: 'A hydrogeological report in addition to the permit' },
      { id: 'c', text: 'Approval from the state legislature' },
      { id: 'd', text: 'Only TCEQ approval' },
    ],
    correctAnswer: 'b',
    explanation: 'Wells with a capacity of 200 gpm or greater in a GCD typically require a hydrogeological report to assess impact on groundwater resources and aquifer sustainability.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q034',
    topic: 'groundwater-districts',
    difficulty: 'advanced',
    question: 'What is the purpose of a Groundwater Conservation District (GCD) in Texas?',
    choices: [
      { id: 'a', text: 'Exclusively for water quality testing' },
      { id: 'b', text: 'To regulate and conserve groundwater and prevent waste' },
      { id: 'c', text: 'To exclusively manage surface water' },
      { id: 'd', text: 'To issue water well contractor licenses' },
    ],
    correctAnswer: 'b',
    explanation: 'GCDs are created under Texas Water Code to regulate groundwater in their territories and manage groundwater resources in the public interest to prevent waste.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q035',
    topic: 'groundwater-districts',
    difficulty: 'intermediate',
    question: 'What happens if a well in a GCD is not completed within the permitted timeframe?',
    choices: [
      { id: 'a', text: 'The permit is automatically extended' },
      { id: 'b', text: 'The permit expires and must be renewed' },
      { id: 'c', text: 'A fine is imposed but the permit continues' },
      { id: 'd', text: 'The property is seized' },
    ],
    correctAnswer: 'b',
    explanation: 'If a well is not completed within the GCD\'s specified completion timeline, the permit expires and the applicant must apply for a new permit.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q036',
    topic: 'groundwater-districts',
    difficulty: 'basic',
    question: 'Which of the following is NOT typically a requirement for well registration in a GCD?',
    choices: [
      { id: 'a', text: 'Well location coordinates' },
      { id: 'b', text: 'Estimated production rate' },
      { id: 'c', text: 'Type of use' },
      { id: 'd', text: 'Owner\'s signature on deed' },
    ],
    correctAnswer: 'd',
    explanation: 'While well registration requires location, production rate, and use information, ownership verification through a deed signature is not typically a registration requirement.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q037',
    topic: 'well-design',
    difficulty: 'basic',
    question: 'Which geologic formation is the primary water-bearing layer of the Trinity Aquifer in the DFW area?',
    choices: [
      { id: 'a', text: 'Paluxy sand' },
      { id: 'b', text: 'Glen Rose limestone' },
      { id: 'c', text: 'Twin Mountains Formation' },
      { id: 'd', text: 'Hosston sandstone' },
    ],
    correctAnswer: 'a',
    explanation: 'The Paluxy sand is the primary productive water-bearing formation of the Trinity Aquifer in the Dallas-Fort Worth area and yields significant groundwater.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q038',
    topic: 'well-design',
    difficulty: 'intermediate',
    question: 'What is the typical depth range for Trinity Aquifer wells in the DFW Metroplex?',
    choices: [
      { id: 'a', text: '50-150 feet' },
      { id: 'b', text: '150-500 feet' },
      { id: 'c', text: '150-1500+ feet' },
      { id: 'd', text: '2000+ feet' },
    ],
    correctAnswer: 'c',
    explanation: 'Trinity Aquifer wells in the DFW area typically range from 150 feet to over 1500 feet deep, depending on location and target formation.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q039',
    topic: 'well-design',
    difficulty: 'intermediate',
    question: 'How is well screen slot size determined during well design?',
    choices: [
      { id: 'a', text: 'Use the largest available slot size' },
      { id: 'b', text: 'Use the smallest available slot size' },
      { id: 'c', text: 'Based on sieve analysis of the formation material' },
      { id: 'd', text: 'Use 0.020 inch slots for all wells' },
    ],
    correctAnswer: 'c',
    explanation: 'Well screen slot size is selected based on sieve analysis of the aquifer formation to retain 80-90% of the formation material while allowing water entry.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q040',
    topic: 'well-design',
    difficulty: 'basic',
    question: 'What is the purpose of a gravel pack in a water well design?',
    choices: [
      { id: 'a', text: 'To support the well casing' },
      { id: 'b', text: 'To improve water flow and prevent sand from entering the well' },
      { id: 'c', text: 'To reduce water temperature' },
      { id: 'd', text: 'To increase well depth' },
    ],
    correctAnswer: 'b',
    explanation: 'A gravel pack is placed around the well screen to improve water flow into the well and prevent formation sand from being drawn into the screen.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q041',
    topic: 'well-design',
    difficulty: 'intermediate',
    question: 'Which of the following formations is part of the Trinity Aquifer Group in Texas?',
    choices: [
      { id: 'a', text: 'Eagle Ford Shale' },
      { id: 'b', text: 'Hosston sandstone' },
      { id: 'c', text: 'Ogallala Formation' },
      { id: 'd', text: 'Frio Formation' },
    ],
    correctAnswer: 'b',
    explanation: 'The Hosston sandstone is part of the Trinity Aquifer Group and is a water-bearing formation, though less productive than the Paluxy sand in most of Texas.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q042',
    topic: 'well-design',
    difficulty: 'basic',
    question: 'What is the Glen Rose Formation in the context of the Trinity Aquifer?',
    choices: [
      { id: 'a', text: 'A limestone aquifer formation' },
      { id: 'b', text: 'A confining layer above the Trinity' },
      { id: 'c', text: 'A clay layer unsuitable for drilling' },
      { id: 'd', text: 'The lowest Trinity Aquifer unit' },
    ],
    correctAnswer: 'a',
    explanation: 'The Glen Rose Formation is a limestone unit within the Trinity Aquifer that contains water in fractures and can produce significant yields in some areas.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q043',
    topic: 'well-design',
    difficulty: 'intermediate',
    question: 'In DFW water well design, what factor determines the number of production zones screened?',
    choices: [
      { id: 'a', text: 'The county where the well is located' },
      { id: 'b', text: 'The depth of water needed and aquifer formation characteristics' },
      { id: 'c', text: 'The well owner\'s preference' },
      { id: 'd', text: 'The well drilling company\'s standard practice' },
    ],
    correctAnswer: 'b',
    explanation: 'The number of screened zones in a well is determined by the desired water depth, formation yields, and aquifer stratigraphy to optimize water production.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q044',
    topic: 'well-design',
    difficulty: 'advanced',
    question: 'What is the purpose of performing a grain size analysis before well completion?',
    choices: [
      { id: 'a', text: 'To determine the well depth' },
      { id: 'b', text: 'To select the appropriate well screen slot size' },
      { id: 'c', text: 'To estimate the well yield' },
      { id: 'd', text: 'To comply with TCEQ reporting requirements' },
    ],
    correctAnswer: 'b',
    explanation: 'A grain size analysis (sieve analysis) of formation samples determines the grain size distribution to select a well screen slot size that prevents sand production.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q045',
    topic: 'well-design',
    difficulty: 'intermediate',
    question: 'How does well casing diameter affect water yield capacity?',
    choices: [
      { id: 'a', text: 'Larger casing diameter increases water yield' },
      { id: 'b', text: 'Casing diameter does not affect water yield' },
      { id: 'c', text: 'Smaller casing diameter increases water yield' },
      { id: 'd', text: 'The relationship is unpredictable' },
    ],
    correctAnswer: 'b',
    explanation: 'Well yield is determined by aquifer properties and screen design, not casing diameter. Casing diameter is selected based on pump size and well use.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q046',
    topic: 'well-design',
    difficulty: 'basic',
    question: 'What is a confined aquifer formation?',
    choices: [
      { id: 'a', text: 'An aquifer with a permeable confining layer above and below' },
      { id: 'b', text: 'An aquifer in a geographic boundary' },
      { id: 'c', text: 'An aquifer that is not producing water' },
      { id: 'd', text: 'An aquifer controlled by the GCD' },
    ],
    correctAnswer: 'a',
    explanation: 'A confined aquifer is bounded above and below by less permeable materials that limit water movement and often result in artesian pressure.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q047',
    topic: 'well-design',
    difficulty: 'intermediate',
    question: 'In the Trinity Aquifer system, why is the Twin Mountains-Hosston formation important in DFW?',
    choices: [
      { id: 'a', text: 'It is the most productive formation' },
      { id: 'b', text: 'It provides backup water supply when Paluxy is depleted' },
      { id: 'c', text: 'It is used exclusively for industrial wells' },
      { id: 'd', text: 'It is the shallowest aquifer formation' },
    ],
    correctAnswer: 'b',
    explanation: 'The Twin Mountains-Hosston formation is important as a deeper, lower-yielding formation that can provide additional water supply when shallower Trinity zones are depleted.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q048',
    topic: 'well-design',
    difficulty: 'basic',
    question: 'What is the typical thickness of the Paluxy sand formation in the DFW area?',
    choices: [
      { id: 'a', text: '10-30 feet' },
      { id: 'b', text: '50-150 feet' },
      { id: 'c', text: '200-500 feet' },
      { id: 'd', text: '1000+ feet' },
    ],
    correctAnswer: 'b',
    explanation: 'The Paluxy sand typically ranges from 50 to 150 feet thick in the DFW area, providing a productive water-bearing formation for well development.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q049',
    topic: 'well-construction',
    difficulty: 'basic',
    question: 'What is the purpose of surface casing in a water well?',
    choices: [
      { id: 'a', text: 'To increase water production' },
      { id: 'b', text: 'To provide structural support and prevent surface contamination' },
      { id: 'c', text: 'To reduce drilling time' },
      { id: 'd', text: 'To maintain constant water temperature' },
    ],
    correctAnswer: 'b',
    explanation: 'Surface casing is installed in the upper portion of the well to provide structural support and prevent surface water and contaminants from entering the well.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q050',
    topic: 'well-construction',
    difficulty: 'basic',
    question: 'What is production casing in a water well?',
    choices: [
      { id: 'a', text: 'The outermost casing at the surface' },
      { id: 'b', text: 'The casing that lines the production zone of the well' },
      { id: 'c', text: 'Temporary casing removed after drilling' },
      { id: 'd', text: 'Flexible plastic casing for small wells' },
    ],
    correctAnswer: 'b',
    explanation: 'Production casing lines the borehole from the bottom of the surface casing to the bottom of the well, protecting the water-bearing formation and the well screen.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q051',
    topic: 'well-construction',
    difficulty: 'intermediate',
    question: 'What is the primary purpose of grouting the annular space in a well?',
    choices: [
      { id: 'a', text: 'To increase water yield' },
      { id: 'b', text: 'To seal off aquifer zones and prevent cross-contamination' },
      { id: 'c', text: 'To support the casing structurally' },
      { id: 'd', text: 'To reduce drilling time' },
    ],
    correctAnswer: 'b',
    explanation: 'Annular space grouting is essential to seal the space between the casing and borehole wall, preventing cross-contamination between aquifer zones.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q052',
    topic: 'well-construction',
    difficulty: 'intermediate',
    question: 'What is the function of a tremie pipe during well grouting?',
    choices: [
      { id: 'a', text: 'To aerate the grout mixture' },
      { id: 'b', text: 'To direct grout into the annular space from the bottom upward' },
      { id: 'c', text: 'To cool the grout during placement' },
      { id: 'd', text: 'To measure grout volume' },
    ],
    correctAnswer: 'b',
    explanation: 'A tremie pipe ensures grout is placed in the annular space from the bottom upward with the discharge end submerged, preventing segregation and voids.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q053',
    topic: 'well-construction',
    difficulty: 'basic',
    question: 'Which development method involves creating surges in the well to break down filter cake?',
    choices: [
      { id: 'a', text: 'Jetting' },
      { id: 'b', text: 'Surging' },
      { id: 'c', text: 'Airlifting' },
      { id: 'd', text: 'Bailing' },
    ],
    correctAnswer: 'b',
    explanation: 'Surging involves rapidly raising and lowering a surge block in the well to create pressure changes that break down the filter cake around the screen.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q054',
    topic: 'well-construction',
    difficulty: 'intermediate',
    question: 'What is airlifting as a well development method?',
    choices: [
      { id: 'a', text: 'Using compressed air to clear debris from the borehole' },
      { id: 'b', text: 'Using a pump to lift the well screen' },
      { id: 'c', text: 'Using air pressure to increase water yield' },
      { id: 'd', text: 'Using air to remove sediment from the surface' },
    ],
    correctAnswer: 'a',
    explanation: 'Airlifting uses compressed air bubbles injected into the well column to lift water and sediment, effectively developing the well by removing fine materials.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q055',
    topic: 'well-construction',
    difficulty: 'basic',
    question: 'What is jetting in the context of well development?',
    choices: [
      { id: 'a', text: 'Using high-pressure water to loosen formation material around the screen' },
      { id: 'b', text: 'Pumping water continuously to develop the well' },
      { id: 'c', text: 'Using air pressure to clear the well' },
      { id: 'd', text: 'Removing sand with a sand pump' },
    ],
    correctAnswer: 'a',
    explanation: 'Jetting uses high-pressure water directed at the formation around the well screen to loosen and remove fine materials, improving permeability.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q056',
    topic: 'well-construction',
    difficulty: 'intermediate',
    question: 'When should well development begin after well installation?',
    choices: [
      { id: 'a', text: 'Immediately after grouting' },
      { id: 'b', text: 'After the grout has cured (typically 24-48 hours)' },
      { id: 'c', text: 'One week after completion' },
      { id: 'd', text: 'Only before the well is put into service' },
    ],
    correctAnswer: 'b',
    explanation: 'Well development typically begins 24-48 hours after grouting to allow adequate grout curing before mechanical disturbance.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q057',
    topic: 'well-construction',
    difficulty: 'basic',
    question: 'What material is used for the sanitary seal in a water well?',
    choices: [
      { id: 'a', text: 'Sand' },
      { id: 'b', text: 'Gravel' },
      { id: 'c', text: 'Neat cement grout' },
      { id: 'd', text: 'Bentonite clay' },
    ],
    correctAnswer: 'c',
    explanation: 'The sanitary seal at the surface is typically constructed from neat cement grout to create a watertight seal preventing surface contamination.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q058',
    topic: 'well-construction',
    difficulty: 'intermediate',
    question: 'How is casing centralizer placement important in well construction?',
    choices: [
      { id: 'a', text: 'It is not important' },
      { id: 'b', text: 'It ensures even grout placement around the casing' },
      { id: 'c', text: 'It increases water production' },
      { id: 'd', text: 'It reduces casing cost' },
    ],
    correctAnswer: 'b',
    explanation: 'Casing centralizers keep the casing centered in the borehole, ensuring uniform annular space width for consistent grout placement.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q059',
    topic: 'well-construction',
    difficulty: 'advanced',
    question: 'What is the recommended well development duration to achieve adequate water quality?',
    choices: [
      { id: 'a', text: '1-2 hours' },
      { id: 'b', text: '4-8 hours' },
      { id: 'c', text: '24-48 hours' },
      { id: 'd', text: 'Until discharge water becomes clear' },
    ],
    correctAnswer: 'd',
    explanation: 'Well development should continue until the discharge water becomes clear or achieves an acceptable turbidity level, typically requiring 24-48+ hours.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q060',
    topic: 'well-construction',
    difficulty: 'basic',
    question: 'What type of drill cuttings should be removed from the borehole before casing installation?',
    choices: [
      { id: 'a', text: 'Only clay cuttings' },
      { id: 'b', text: 'All drill cuttings and drilling mud' },
      { id: 'c', text: 'Only sand cuttings' },
      { id: 'd', text: 'Cuttings do not need to be removed' },
    ],
    correctAnswer: 'b',
    explanation: 'All drill cuttings and drilling mud must be removed from the borehole before casing installation to ensure proper contact and grouting.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q061',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What does LOTO stand for in electrical safety?',
    choices: [
      { id: 'a', text: 'Low Tolerance Operator' },
      { id: 'b', text: 'Lockout/Tagout' },
      { id: 'c', text: 'Long-term Operational Training' },
      { id: 'd', text: 'Load Test Operation' },
    ],
    correctAnswer: 'b',
    explanation: 'LOTO (Lockout/Tagout) is the process of securing energy sources to prevent accidental equipment startup during maintenance or service work.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q062',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What is the standard voltage for residential water well systems in the United States?',
    choices: [
      { id: 'a', text: '120 volts' },
      { id: 'b', text: '240 volts' },
      { id: 'c', text: '480 volts' },
      { id: 'd', text: '600 volts' },
    ],
    correctAnswer: 'b',
    explanation: 'Residential water well systems typically operate at 240 volts (single-phase) for submersible pumps, though some larger systems may use 480 volts (three-phase).',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q063',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'How is amperage calculated using Ohm\'s Law?',
    choices: [
      { id: 'a', text: 'Voltage × Resistance' },
      { id: 'b', text: 'Voltage ÷ Resistance' },
      { id: 'c', text: 'Resistance ÷ Voltage' },
      { id: 'd', text: 'Voltage + Resistance' },
    ],
    correctAnswer: 'b',
    explanation: 'Amperage (I) is calculated using Ohm\'s Law: I = V ÷ R, where V is voltage and R is resistance in ohms.',
    isMath: true,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q064',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What does three-phase power provide compared to single-phase power?',
    choices: [
      { id: 'a', text: 'Higher voltage only' },
      { id: 'b', text: 'More stable power and better efficiency' },
      { id: 'c', text: 'Lower cost only' },
      { id: 'd', text: 'No practical difference' },
    ],
    correctAnswer: 'b',
    explanation: 'Three-phase power provides more stable, continuous power delivery and operates more efficiently than single-phase, requiring smaller wire sizes.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q065',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'How is wire size determined for electrical circuits?',
    choices: [
      { id: 'a', text: 'Based on wire cost' },
      { id: 'b', text: 'Based on amperage requirements and distance from the power source' },
      { id: 'c', text: 'Based on voltage only' },
      { id: 'd', text: 'Arbitrarily selected' },
    ],
    correctAnswer: 'b',
    explanation: 'Wire size is determined by amperage requirements and distance from the power source to prevent voltage drop and overheating.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q066',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What is voltage drop in an electrical circuit?',
    choices: [
      { id: 'a', text: 'Loss of power during a storm' },
      { id: 'b', text: 'Reduction in voltage along a conductor due to resistance' },
      { id: 'c', text: 'Intentional voltage reduction' },
      { id: 'd', text: 'A circuit breaker fault' },
    ],
    correctAnswer: 'b',
    explanation: 'Voltage drop is the reduction in voltage that occurs as current travels through a conductor, calculated as V = I × R.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q067',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What percentage voltage drop is considered acceptable in well pump circuits?',
    choices: [
      { id: 'a', text: '1%' },
      { id: 'b', text: '3%' },
      { id: 'c', text: '5%' },
      { id: 'd', text: '10%' },
    ],
    correctAnswer: 'b',
    explanation: 'Most standards recommend a maximum voltage drop of 3% for branch circuits (5% total for feeder and branch combined) to maintain equipment efficiency.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q068',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What is the purpose of grounding in electrical systems?',
    choices: [
      { id: 'a', text: 'To increase voltage' },
      { id: 'b', text: 'To provide a safe path for fault current and reduce shock hazard' },
      { id: 'c', text: 'To reduce water usage' },
      { id: 'd', text: 'To store electrical energy' },
    ],
    correctAnswer: 'b',
    explanation: 'Grounding provides a safe return path for fault current and reduces the risk of electrical shock by maintaining a reference at zero potential.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q069',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What is the difference between grounding and bonding in electrical systems?',
    choices: [
      { id: 'a', text: 'They are the same thing' },
      { id: 'b', text: 'Grounding connects to earth; bonding connects conductive parts together' },
      { id: 'c', text: 'Grounding is for AC, bonding is for DC' },
      { id: 'd', text: 'Bonding is more important than grounding' },
    ],
    correctAnswer: 'b',
    explanation: 'Grounding establishes a connection to earth, while bonding connects conductive parts together to maintain equipotential surfaces and reduce shock risk.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q070',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What is the function of a pressure switch in a well system?',
    choices: [
      { id: 'a', text: 'To measure water flow rate' },
      { id: 'b', text: 'To start and stop the pump based on pressure' },
      { id: 'c', text: 'To filter water' },
      { id: 'd', text: 'To increase water temperature' },
    ],
    correctAnswer: 'b',
    explanation: 'A pressure switch automatically starts the pump when pressure drops below a set point (cut-in) and stops when it reaches the cut-out pressure.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q071',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What is a multimeter used for in well system diagnostics?',
    choices: [
      { id: 'a', text: 'Measuring water flow' },
      { id: 'b', text: 'Measuring voltage, current, and resistance' },
      { id: 'c', text: 'Adjusting pressure switches' },
      { id: 'd', text: 'Testing water quality' },
    ],
    correctAnswer: 'b',
    explanation: 'A multimeter measures voltage, current (amperage), and resistance to diagnose electrical problems in well systems.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q072',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What does a megger test measure in electrical systems?',
    choices: [
      { id: 'a', text: 'Voltage level' },
      { id: 'b', text: 'Insulation resistance between conductors' },
      { id: 'c', text: 'Water pressure' },
      { id: 'd', text: 'Wire gauge' },
    ],
    correctAnswer: 'b',
    explanation: 'A megger (insulation resistance tester) measures the resistance of insulation between conductors, important for safety testing of electrical equipment.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q073',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What type of conduit is required for electrical wire protection in well installations?',
    choices: [
      { id: 'a', text: 'PVC only' },
      { id: 'b', text: 'Metallic or PVC rated for the environment' },
      { id: 'c', text: 'Wire can be exposed without conduit' },
      { id: 'd', text: 'Flexible garden hose' },
    ],
    correctAnswer: 'b',
    explanation: 'NEC code requires that well pump wires be protected in either metallic conduit (EMT) or schedule 40/80 PVC conduit rated for underground use.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q074',
    topic: 'electrical-safety',
    difficulty: 'advanced',
    question: 'How is wire ampacity determined according to the National Electrical Code (NEC)?',
    choices: [
      { id: 'a', text: 'Based on wire color' },
      { id: 'b', text: 'Based on wire gauge, insulation type, and ambient temperature' },
      { id: 'c', text: 'Based on wire cost' },
      { id: 'd', text: 'Ampacity is not regulated' },
    ],
    correctAnswer: 'b',
    explanation: 'Wire ampacity is determined by wire gauge (AWG), insulation temperature rating, and ambient temperature conditions per NEC tables.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q075',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What is the function of a circuit breaker in a well system?',
    choices: [
      { id: 'a', text: 'To increase water pressure' },
      { id: 'b', text: 'To protect the circuit from overcurrent' },
      { id: 'c', text: 'To reduce electrical noise' },
      { id: 'd', text: 'To store electrical energy' },
    ],
    correctAnswer: 'b',
    explanation: 'A circuit breaker automatically opens (trips) when current exceeds its rating, protecting wiring and equipment from damage and fire hazard.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q076',
    topic: 'electrical-safety',
    difficulty: 'advanced',
    question: 'What is an arc flash hazard in electrical work?',
    choices: [
      { id: 'a', text: 'A brief light caused by opening a switch' },
      { id: 'b', text: 'A violent explosion of ionized air resulting from an arc fault' },
      { id: 'c', text: 'A type of circuit breaker' },
      { id: 'd', text: 'A protection device for transformers' },
    ],
    correctAnswer: 'b',
    explanation: 'An arc flash is a violent release of energy when an electric arc forms between conductors, capable of causing severe burns and injuries.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q077',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What precaution should be taken before working on well pump electrical systems?',
    choices: [
      { id: 'a', text: 'No special precautions needed' },
      { id: 'b', text: 'Turn off the circuit breaker and lock it in the OFF position' },
      { id: 'c', text: 'Just turn off the pump switch' },
      { id: 'd', text: 'Wear rubber gloves only' },
    ],
    correctAnswer: 'b',
    explanation: 'Before working on electrical systems, the circuit breaker must be switched OFF and locked in that position (LOTO procedure) to prevent accidental energization.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q078',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What is a GFCI outlet?',
    choices: [
      { id: 'a', text: 'A high-capacity electrical outlet' },
      { id: 'b', text: 'Ground Fault Circuit Interrupter - detects ground faults and cuts power' },
      { id: 'c', text: 'A weatherproof outlet' },
      { id: 'd', text: 'An outlet for grounding purposes' },
    ],
    correctAnswer: 'b',
    explanation: 'A GFCI (Ground Fault Circuit Interrupter) detects ground faults and rapidly shuts off power, protecting against electrical shock in wet environments.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q079',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What is the significance of a dead-front disconnect switch?',
    choices: [
      { id: 'a', text: 'It has no power supply' },
      { id: 'b', text: 'It has no exposed live parts when opened' },
      { id: 'c', text: 'It prevents water from entering' },
      { id: 'd', text: 'It is located in front of the well' },
    ],
    correctAnswer: 'b',
    explanation: 'A dead-front disconnect switch has no exposed live parts when in the open position, providing safer access for maintenance and service.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q080',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What is the maximum safe voltage considered low voltage?',
    choices: [
      { id: 'a', text: '50 volts' },
      { id: 'b', text: '120 volts' },
      { id: 'c', text: '240 volts' },
      { id: 'd', text: '480 volts' },
    ],
    correctAnswer: 'a',
    explanation: 'Voltages above 50 volts are considered high voltage and present significant shock hazards. Below 50 volts is typically considered low voltage or extra low voltage.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q081',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'What is a submersible pump in the context of water wells?',
    choices: [
      { id: 'a', text: 'A pump located above the water surface' },
      { id: 'b', text: 'A pump designed to operate completely submerged in water' },
      { id: 'c', text: 'A pump that filters sediment' },
      { id: 'd', text: 'A temporary pump for drilling' },
    ],
    correctAnswer: 'b',
    explanation: 'A submersible pump is designed to operate fully submerged in water and is commonly used in water wells for efficient pumping from depth.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q082',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'What are the main components of a submersible pump?',
    choices: [
      { id: 'a', text: 'Impeller, shaft, and housing' },
      { id: 'b', text: 'Motor, pump head, and cable' },
      { id: 'c', text: 'Motor, impeller, discharge head, and cable' },
      { id: 'd', text: 'Only an electric motor' },
    ],
    correctAnswer: 'c',
    explanation: 'A submersible pump consists of a submersible motor, pump head with impeller, discharge head directing flow, and electrical cable for power.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q083',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'How is pump size selected for a water well?',
    choices: [
      { id: 'a', text: 'Based on well depth only' },
      { id: 'b', text: 'Based on desired flow rate and head pressure needed' },
      { id: 'c', text: 'Based on casing diameter only' },
      { id: 'd', text: 'The largest pump available' },
    ],
    correctAnswer: 'b',
    explanation: 'Pump sizing is based on the required flow rate (gpm) and the total dynamic head (TDH) that includes depth, friction losses, and system pressure requirements.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q084',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'What is a pressure tank in a water well system?',
    choices: [
      { id: 'a', text: 'A tank that stores well components' },
      { id: 'b', text: 'A tank that maintains water pressure and reduces pump cycling' },
      { id: 'c', text: 'A tank that filters water' },
      { id: 'd', text: 'A tank for chemical storage' },
    ],
    correctAnswer: 'b',
    explanation: 'A pressure tank is a pressurized vessel that maintains system pressure, stores water, and reduces the frequency of pump starts and stops.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q085',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'What is the cut-in pressure of a pump system?',
    choices: [
      { id: 'a', text: 'The maximum operating pressure' },
      { id: 'b', text: 'The pressure at which the pump starts' },
      { id: 'c', text: 'The pressure at which the pump stops' },
      { id: 'd', text: 'The pressure at the wellhead' },
    ],
    correctAnswer: 'b',
    explanation: 'Cut-in pressure is the preset pressure at which the pressure switch closes and starts the pump, typically 10-20 psi below cut-out pressure.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q086',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'What is the cut-out pressure of a pump system?',
    choices: [
      { id: 'a', text: 'The minimum operating pressure' },
      { id: 'b', text: 'The pressure at which the pump starts' },
      { id: 'c', text: 'The pressure at which the pump stops' },
      { id: 'd', text: 'The pressure gauge reading' },
    ],
    correctAnswer: 'c',
    explanation: 'Cut-out pressure is the preset pressure at which the pressure switch opens and stops the pump, preventing excessive pressure and tank strain.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q087',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'What is a variable frequency drive (VFD) in pump control?',
    choices: [
      { id: 'a', text: 'A mechanical coupling system' },
      { id: 'b', text: 'A device that varies motor speed to maintain constant pressure' },
      { id: 'c', text: 'A pressure gauge' },
      { id: 'd', text: 'A type of pump impeller' },
    ],
    correctAnswer: 'b',
    explanation: 'A VFD adjusts motor speed based on system demand to maintain constant pressure, providing energy efficiency and reduced pump cycling.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q088',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'What is a pitless adapter in a water well?',
    choices: [
      { id: 'a', text: 'A device that removes sediment' },
      { id: 'b', text: 'A below-ground connection allowing discharge below the wellhead' },
      { id: 'c', text: 'A pump component' },
      { id: 'd', text: 'A type of well casing' },
    ],
    correctAnswer: 'b',
    explanation: 'A pitless adapter allows water to exit the well below ground level, preventing freezing and providing cleaner aesthetics without a visible pit.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q089',
    topic: 'pump-systems',
    difficulty: 'advanced',
    question: 'What is total dynamic head (TDH) in pump selection?',
    choices: [
      { id: 'a', text: 'The static water level depth' },
      { id: 'b', text: 'The sum of elevation gain, friction losses, and system pressure' },
      { id: 'c', text: 'The pump horsepower' },
      { id: 'd', text: 'The well diameter' },
    ],
    correctAnswer: 'b',
    explanation: 'TDH is the total pressure the pump must overcome, including static lift, drawdown, elevation difference, and friction losses in the system.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q090',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'What is the purpose of a check valve in a well discharge line?',
    choices: [
      { id: 'a', text: 'To measure water flow' },
      { id: 'b', text: 'To prevent backflow of water into the well' },
      { id: 'c', text: 'To reduce water temperature' },
      { id: 'd', text: 'To filter sediment' },
    ],
    correctAnswer: 'b',
    explanation: 'A check valve allows water to flow in one direction only, preventing backflow and siphoning that could damage the pump or contaminate the well.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q091',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'How does pump efficiency affect total system cost?',
    choices: [
      { id: 'a', text: 'It has no impact' },
      { id: 'b', text: 'Higher efficiency reduces operating costs over time' },
      { id: 'c', text: 'Higher efficiency is only marketing' },
      { id: 'd', text: 'Efficiency only affects pump noise' },
    ],
    correctAnswer: 'b',
    explanation: 'A more efficient pump requires less energy to produce the same flow, significantly reducing electricity costs over the pump\'s lifetime.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q092',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'What is a pump curve?',
    choices: [
      { id: 'a', text: 'The physical shape of the pump housing' },
      { id: 'b', text: 'A graph showing flow rate vs. head for a given pump' },
      { id: 'c', text: 'The electrical connection diagram' },
      { id: 'd', text: 'A maintenance schedule' },
    ],
    correctAnswer: 'b',
    explanation: 'A pump curve is a graph provided by manufacturers showing the relationship between flow rate and pressure head for a specific pump model.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q093',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'What is cavitation in pump systems?',
    choices: [
      { id: 'a', text: 'Normal pump operation' },
      { id: 'b', text: 'Formation and collapse of vapor bubbles causing damage' },
      { id: 'c', text: 'Excessive water pressure' },
      { id: 'd', text: 'Sediment accumulation' },
    ],
    correctAnswer: 'b',
    explanation: 'Cavitation occurs when pressure drops below water vapor pressure, creating bubbles that collapse and cause noise, vibration, and pump damage.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q094',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'What is the typical voltage for submersible pump motors in residential wells?',
    choices: [
      { id: 'a', text: '120 volts single-phase' },
      { id: 'b', text: '240 volts single-phase' },
      { id: 'c', text: '480 volts three-phase' },
      { id: 'd', text: '600 volts three-phase' },
    ],
    correctAnswer: 'b',
    explanation: 'Most residential submersible pump motors operate at 240 volts single-phase, with three-phase 480V used for larger commercial installations.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q095',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'What does pump horsepower (HP) indicate?',
    choices: [
      { id: 'a', text: 'The physical size of the pump' },
      { id: 'b', text: 'The maximum flow rate achievable' },
      { id: 'c', text: 'The rate at which the pump does work' },
      { id: 'd', text: 'The depth of the well' },
    ],
    correctAnswer: 'c',
    explanation: 'Horsepower (HP) measures the rate at which a pump does work, calculated as the flow rate and pressure needed divided by efficiency.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q096',
    topic: 'water-quality',
    difficulty: 'basic',
    question: 'What is the typical hardness range of water in the DFW area?',
    choices: [
      { id: 'a', text: '50-100 mg/L' },
      { id: 'b', text: '121-180 mg/L' },
      { id: 'c', text: '200-250 mg/L' },
      { id: 'd', text: '300+ mg/L' },
    ],
    correctAnswer: 'b',
    explanation: 'Water in the DFW area typically has a hardness of 121-180 mg/L (as CaCO3), considered moderately to very hard water.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q097',
    topic: 'water-quality',
    difficulty: 'basic',
    question: 'At what iron concentration does water typically cause staining?',
    choices: [
      { id: 'a', text: '0.1 ppm' },
      { id: 'b', text: '0.2 ppm' },
      { id: 'c', text: '0.3 ppm' },
      { id: 'd', text: '0.5 ppm' },
    ],
    correctAnswer: 'c',
    explanation: 'Iron staining typically becomes noticeable at concentrations above 0.3 ppm, affecting laundry and fixtures.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q098',
    topic: 'water-quality',
    difficulty: 'basic',
    question: 'What is the secondary drinking water standard for manganese?',
    choices: [
      { id: 'a', text: '0.01 ppm' },
      { id: 'b', text: '0.05 ppm' },
      { id: 'c', text: '0.1 ppm' },
      { id: 'd', text: '0.5 ppm' },
    ],
    correctAnswer: 'b',
    explanation: 'The EPA secondary drinking water standard for manganese is 0.05 ppm to prevent staining and discoloration.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q099',
    topic: 'water-quality',
    difficulty: 'basic',
    question: 'What hydrogen sulfide (H2S) concentration is noticeable by smell?',
    choices: [
      { id: 'a', text: '0.05 ppm' },
      { id: 'b', text: '0.1 ppm' },
      { id: 'c', text: '0.3 ppm' },
      { id: 'd', text: '1.0 ppm' },
    ],
    correctAnswer: 'c',
    explanation: 'Hydrogen sulfide becomes noticeable as a rotten egg smell at concentrations above 0.3 ppm, though the secondary standard is 0.05 ppm.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q100',
    topic: 'water-quality',
    difficulty: 'basic',
    question: 'What does TDS stand for in water quality testing?',
    choices: [
      { id: 'a', text: 'Total Dissolved Solids' },
      { id: 'b', text: 'Total Discharge Standard' },
      { id: 'c', text: 'Turbidity Density Suspension' },
      { id: 'd', text: 'Temperature Dependent Salinity' },
    ],
    correctAnswer: 'a',
    explanation: 'TDS (Total Dissolved Solids) represents all dissolved mineral content in water, measured in mg/L or ppm.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q101',
    topic: 'water-quality',
    difficulty: 'intermediate',
    question: 'What is a major health concern with radionuclides in drinking water?',
    choices: [
      { id: 'a', text: 'They cause taste and odor' },
      { id: 'b', text: 'They cause radioactive decay and potential cancer risk' },
      { id: 'c', text: 'They increase water hardness' },
      { id: 'd', text: 'They have no health concerns' },
    ],
    correctAnswer: 'b',
    explanation: 'Radionuclides are radioactive elements that pose a health risk through radiation exposure, increasing cancer and other disease risks.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q102',
    topic: 'water-quality',
    difficulty: 'basic',
    question: 'What is the primary source of coliform bacteria in groundwater?',
    choices: [
      { id: 'a', text: 'Natural aquifer minerals' },
      { id: 'b', text: 'Surface contamination from sewage or animal waste' },
      { id: 'c', text: 'Dissolved gases' },
      { id: 'd', text: 'Well casing material' },
    ],
    correctAnswer: 'b',
    explanation: 'Coliform bacteria primarily come from fecal material and sewage contamination, indicating potential pathogenic contamination.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q103',
    topic: 'water-treatment',
    difficulty: 'basic',
    question: 'What is the primary purpose of a water softener?',
    choices: [
      { id: 'a', text: 'To remove bacteria' },
      { id: 'b', text: 'To remove calcium and magnesium (hardness)' },
      { id: 'c', text: 'To improve water taste' },
      { id: 'd', text: 'To reduce water temperature' },
    ],
    correctAnswer: 'b',
    explanation: 'A water softener removes hardness minerals (calcium and magnesium) through ion exchange, reducing scale and improving soaps effectiveness.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q104',
    topic: 'water-treatment',
    difficulty: 'intermediate',
    question: 'What contaminant do iron filters primarily remove?',
    choices: [
      { id: 'a', text: 'Bacteria and viruses' },
      { id: 'b', text: 'Iron and manganese' },
      { id: 'c', text: 'Hardness minerals' },
      { id: 'd', text: 'Chlorine' },
    ],
    correctAnswer: 'b',
    explanation: 'Iron filters oxidize and remove dissolved iron and manganese, preventing staining and discoloration of water.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q105',
    topic: 'water-treatment',
    difficulty: 'basic',
    question: 'What is the purpose of chlorination in water treatment?',
    choices: [
      { id: 'a', text: 'To remove hardness' },
      { id: 'b', text: 'To disinfect water and kill bacteria' },
      { id: 'c', text: 'To improve taste' },
      { id: 'd', text: 'To lower pH' },
    ],
    correctAnswer: 'b',
    explanation: 'Chlorination adds chlorine to disinfect water and kill bacteria, viruses, and other pathogens that could cause disease.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q106',
    topic: 'water-treatment',
    difficulty: 'intermediate',
    question: 'What does catalytic carbon remove from water?',
    choices: [
      { id: 'a', text: 'Bacteria and protozoa' },
      { id: 'b', text: 'Chlorine and hydrogen sulfide' },
      { id: 'c', text: 'Hardness minerals' },
      { id: 'd', text: 'Iron and manganese' },
    ],
    correctAnswer: 'b',
    explanation: 'Catalytic carbon removes chlorine (through adsorption) and hydrogen sulfide (through oxidation), improving taste and odor.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q107',
    topic: 'water-treatment',
    difficulty: 'intermediate',
    question: 'What is reverse osmosis (RO) primarily used for?',
    choices: [
      { id: 'a', text: 'To remove hardness' },
      { id: 'b', text: 'To remove dissolved solids and most contaminants' },
      { id: 'c', text: 'To add minerals to water' },
      { id: 'd', text: 'To increase water pressure' },
    ],
    correctAnswer: 'b',
    explanation: 'Reverse osmosis forces water through a semi-permeable membrane, removing dissolved solids, bacteria, and most contaminants.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q108',
    topic: 'water-treatment',
    difficulty: 'basic',
    question: 'What is an MCL in drinking water standards?',
    choices: [
      { id: 'a', text: 'Minimum Contaminant Level' },
      { id: 'b', text: 'Maximum Contaminant Level' },
      { id: 'c', text: 'Mandatory Chlorine Level' },
      { id: 'd', text: 'Mineral Content Limit' },
    ],
    correctAnswer: 'b',
    explanation: 'MCL (Maximum Contaminant Level) is the highest concentration of a contaminant allowed in drinking water by EPA regulations.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q109',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What is the primary purpose of OSHA in water well operations?',
    choices: [
      { id: 'a', text: 'To regulate water quality' },
      { id: 'b', text: 'To ensure worker safety and health' },
      { id: 'c', text: 'To manage groundwater resources' },
      { id: 'd', text: 'To issue water well permits' },
    ],
    correctAnswer: 'b',
    explanation: 'OSHA (Occupational Safety and Health Administration) ensures safe working conditions and health protection for employees in water well drilling.',
    isMath: false,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q110',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What is a critical factor in safe hoist and rigging operations?',
    choices: [
      { id: 'a', text: 'The color of the rigging equipment' },
      { id: 'b', text: 'The time of day operations occur' },
      { id: 'c', text: 'Load rating verification and proper rigging techniques' },
      { id: 'd', text: 'The brand of equipment used' },
    ],
    correctAnswer: 'c',
    explanation: 'Safe hoisting requires verifying load ratings, using proper rigging techniques, and maintaining equipment to prevent failures and injuries.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  // Safety & OSHA - q111-q130 (20 questions)
  {
    id: 'q111',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What is the OSHA requirement for fall protection above what height?',
    choices: [
      { id: 'a', text: '4 feet' },
      { id: 'b', text: '6 feet' },
      { id: 'c', text: '8 feet' },
      { id: 'd', text: '10 feet' },
    ],
    correctAnswer: 'b',
    explanation: 'Per OSHA 1926.501, fall protection is required for work above 6 feet. This is the standard height threshold for fall protection measures.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q112',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What does OSHA 1910.146 regulate?',
    choices: [
      { id: 'a', text: 'Electrical safety requirements' },
      { id: 'b', text: 'Confined space entry' },
      { id: 'c', text: 'Fall protection' },
      { id: 'd', text: 'Trenching procedures' },
    ],
    correctAnswer: 'b',
    explanation: 'OSHA 1910.146 covers permit-required confined spaces, including atmospheric testing, rescue procedures, and entry permits.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q113',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What atmospheric hazards must be tested before confined space entry per 1910.146?',
    choices: [
      { id: 'a', text: 'Only oxygen levels' },
      { id: 'b', text: 'Oxygen, flammable gases, and toxic gases' },
      { id: 'c', text: 'Only carbon dioxide' },
      { id: 'd', text: 'Temperature and humidity' },
    ],
    correctAnswer: 'b',
    explanation: 'OSHA 1910.146 requires testing for oxygen (19.5-23.5%), flammable gases (<25% LEL), and toxic gases before entering permit spaces.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q114',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What is required before entering a permit-required confined space?',
    choices: [
      { id: 'a', text: 'Worker notification only' },
      { id: 'b', text: 'Atmospheric testing and written permit' },
      { id: 'c', text: 'Supervisor approval only' },
      { id: 'd', text: 'Tool inventory check' },
    ],
    correctAnswer: 'b',
    explanation: 'Permit-required confined spaces require atmospheric testing, written entry permits, trained attendants, and rescue procedures per 1910.146.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q115',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What does a confined space rescue procedure include?',
    choices: [
      { id: 'a', text: 'Emergency contact numbers only' },
      { id: 'b', text: 'Trained rescue team and retrieval equipment' },
      { id: 'c', text: 'First aid kit' },
      { id: 'd', text: 'Flashlight and radio' },
    ],
    correctAnswer: 'b',
    explanation: 'Confined space rescue requires a trained rescue team, retrieval equipment, and procedures to safely extract an injured worker.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q116',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What OSHA standard covers trenching and excavation safety?',
    choices: [
      { id: 'a', text: '1926.500' },
      { id: 'b', text: '1926.550' },
      { id: 'c', text: '1926.650-652' },
      { id: 'd', text: '1910.200' },
    ],
    correctAnswer: 'c',
    explanation: 'OSHA 1926.650-652 covers trenching and excavation safety, including slope/angle requirements, shoring, and trench protection.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q117',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What is the purpose of calling 811 before trenching?',
    choices: [
      { id: 'a', text: 'To get weather updates' },
      { id: 'b', text: 'To locate underground utilities and prevent damage' },
      { id: 'c', text: 'To request equipment rental' },
      { id: 'd', text: 'To schedule permit inspections' },
    ],
    correctAnswer: 'b',
    explanation: 'Calling 811 (Call Before You Dig) ensures underground utilities (gas, electric, water, telecommunications) are marked before excavation to prevent accidents.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q118',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What sloping angle is typically required for trenches 5 feet deep in Type C soil per OSHA 1926.652?',
    choices: [
      { id: 'a', text: '45 degrees' },
      { id: 'b', text: '60 degrees' },
      { id: 'c', text: '90 degrees (vertical)' },
      { id: 'd', text: '30 degrees' },
    ],
    correctAnswer: 'a',
    explanation: 'OSHA 1926.652 requires sloping at 45 degrees for Type C soil (poor soil conditions). Steeper angles or shoring may be needed for deeper trenches.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q119',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What is the primary function of shoring in a trench?',
    choices: [
      { id: 'a', text: 'To improve water drainage' },
      { id: 'b', text: 'To support trench walls and prevent collapse' },
      { id: 'c', text: 'To measure trench depth' },
      { id: 'd', text: 'To mark utility lines' },
    ],
    correctAnswer: 'b',
    explanation: 'Shoring systems (timber, aluminum, or steel) support trench walls under load to prevent cave-ins and protect workers in the trench.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q120',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What does PPE stand for in safety protocols?',
    choices: [
      { id: 'a', text: 'Personal Protection Equipment' },
      { id: 'b', text: 'Protective Personnel Equipment' },
      { id: 'c', text: 'Personal Protective Equipment' },
      { id: 'd', text: 'Professional Protective Essentials' },
    ],
    correctAnswer: 'c',
    explanation: 'PPE (Personal Protective Equipment) includes hard hats, gloves, safety glasses, steel-toed boots, and respirators to protect workers from hazards.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q121',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'When is a hard hat required in well drilling operations?',
    choices: [
      { id: 'a', text: 'Only when operating machinery' },
      { id: 'b', text: 'Only during drilling' },
      { id: 'c', text: 'At all times on the jobsite' },
      { id: 'd', text: 'Only in underground work' },
    ],
    correctAnswer: 'c',
    explanation: 'Hard hats must be worn at all times on the jobsite to protect from falling objects, overhead hazards, and bumping into structures.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q122',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What is the primary cause of heat stress in well drilling?',
    choices: [
      { id: 'a', text: 'Excessive machinery noise' },
      { id: 'b', text: 'Prolonged exposure to high temperatures and physical exertion' },
      { id: 'c', text: 'Dehydration from drilling fluids' },
      { id: 'd', text: 'Pressure from drilling equipment' },
    ],
    correctAnswer: 'b',
    explanation: 'Heat stress occurs from prolonged exposure to high ambient temperatures combined with physical labor, leading to dehydration and potential heat illness.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q123',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What preventive measures reduce heat stress during drilling operations?',
    choices: [
      { id: 'a', text: 'Working longer shifts' },
      { id: 'b', text: 'Drinking coffee and energy drinks' },
      { id: 'c', text: 'Regular breaks, hydration, and light-colored clothing' },
      { id: 'd', text: 'Accelerating drilling speed' },
    ],
    correctAnswer: 'c',
    explanation: 'Heat stress prevention includes frequent breaks in shade, drinking water, wearing light-colored breathable clothing, and monitoring for heat illness symptoms.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q124',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What are the primary objectives of first aid training in the field?',
    choices: [
      { id: 'a', text: 'To diagnose illnesses' },
      { id: 'b', text: 'To stabilize injured workers and provide immediate care' },
      { id: 'c', text: 'To treat all medical conditions' },
      { id: 'd', text: 'To replace professional medical care' },
    ],
    correctAnswer: 'b',
    explanation: 'First aid training enables workers to stabilize injured persons, control bleeding, provide CPR, and maintain airways until professional medical help arrives.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q125',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What should be the first action in a jobsite medical emergency?',
    choices: [
      { id: 'a', text: 'Move the victim to the truck' },
      { id: 'b', text: 'Call 911 and provide first aid while waiting' },
      { id: 'c', text: 'Attempt to treat the injury without help' },
      { id: 'd', text: 'Document the incident' },
    ],
    correctAnswer: 'b',
    explanation: 'In a medical emergency, immediately call 911 to summon professional help while providing appropriate first aid and keeping the victim stable.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q126',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What is an emergency procedure in well drilling?',
    choices: [
      { id: 'a', text: 'A method to speed up drilling' },
      { id: 'b', text: 'A plan for evacuating workers and responding to incidents' },
      { id: 'c', text: 'A maintenance schedule' },
      { id: 'd', text: 'A training class' },
    ],
    correctAnswer: 'b',
    explanation: 'Emergency procedures establish protocols for worker evacuation, incident response, medical care, and communication during safety-critical events.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q127',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What should be included in a jobsite emergency plan?',
    choices: [
      { id: 'a', text: 'Only contact numbers' },
      { id: 'b', text: 'Evacuation routes, assembly points, emergency contacts, and communication methods' },
      { id: 'c', text: 'Equipment inventory' },
      { id: 'd', text: 'Weather forecasts' },
    ],
    correctAnswer: 'b',
    explanation: 'Emergency plans must include evacuation routes, designated assembly points, emergency contact information, and communication procedures for all staff.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q128',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What is a near-miss in jobsite safety?',
    choices: [
      { id: 'a', text: 'An actual injury to a worker' },
      { id: 'b', text: 'An incident that could have caused injury but did not' },
      { id: 'c', text: 'A missed safety meeting' },
      { id: 'd', text: 'A mistake in paperwork' },
    ],
    correctAnswer: 'b',
    explanation: 'A near-miss is an incident that did not result in injury but had the potential to do so, providing opportunities to prevent future accidents.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q129',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'Why should near-miss incidents be reported?',
    choices: [
      { id: 'a', text: 'To blame workers' },
      { id: 'b', text: 'To identify hazards and prevent future injuries' },
      { id: 'c', text: 'To reduce paperwork' },
      { id: 'd', text: 'For disciplinary purposes' },
    ],
    correctAnswer: 'b',
    explanation: 'Reporting near-misses identifies system hazards and allows corrective actions before someone is seriously injured, improving overall safety.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q130',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'What is lockout/tagout (LOTO) in equipment safety?',
    choices: [
      { id: 'a', text: 'Closing the door to equipment storage' },
      { id: 'b', text: 'Physically locking out and labeling equipment during maintenance to prevent unexpected startup' },
      { id: 'c', text: 'Recording equipment usage' },
      { id: 'd', text: 'Testing equipment before use' },
    ],
    correctAnswer: 'b',
    explanation: 'Lockout/Tagout (LOTO) procedures physically disable and label equipment during maintenance/service to prevent accidental startup and worker injury.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  // Hoist Operations - q131-q150 (20 questions)
  {
    id: 'q131',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What is safe lifting capacity in hoist operations?',
    choices: [
      { id: 'a', text: 'The maximum weight a operator feels comfortable lifting' },
      { id: 'b', text: 'The maximum weight stamped on the equipment nameplate' },
      { id: 'c', text: 'The weight of the load plus the hoist weight' },
      { id: 'd', text: 'An estimate based on experience' },
    ],
    correctAnswer: 'b',
    explanation: 'Safe lifting capacity is the manufacturer-specified maximum load rating clearly stamped on the hoist equipment and load charts.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q132',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What is the purpose of a load chart in crane/hoist operations?',
    choices: [
      { id: 'a', text: 'To track maintenance schedules' },
      { id: 'b', text: 'To show safe working loads at various boom angles and radii' },
      { id: 'c', text: 'To record daily operations' },
      { id: 'd', text: 'To calculate operational costs' },
    ],
    correctAnswer: 'b',
    explanation: 'Load charts provide safe working load limits based on boom angle, boom length, and radius, ensuring operations remain within safe parameters.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q133',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What factor significantly reduces hoist load capacity?',
    choices: [
      { id: 'a', text: 'Weather conditions' },
      { id: 'b', text: 'Increased boom angle away from vertical' },
      { id: 'c', text: 'The time of day' },
      { id: 'd', text: 'Operator experience' },
    ],
    correctAnswer: 'b',
    explanation: 'As boom angle increases (extended horizontally), load capacity decreases due to increased moment arm and stress on boom structure.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q134',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What do standardized hand signals in hoisting operations prevent?',
    choices: [
      { id: 'a', text: 'Equipment noise' },
      { id: 'b', text: 'Miscommunication between operator and signal person' },
      { id: 'c', text: 'Long delays' },
      { id: 'd', text: 'Maintenance issues' },
    ],
    correctAnswer: 'b',
    explanation: 'Standardized hand signals ensure clear communication between the signal person and hoist operator when verbal communication is not possible.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q135',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What is the hand signal for stopping a hoist?',
    choices: [
      { id: 'a', text: 'One hand raised' },
      { id: 'b', text: 'Both arms raised horizontally' },
      { id: 'c', text: 'Arms crossed over the head' },
      { id: 'd', text: 'Hand pointing downward' },
    ],
    correctAnswer: 'c',
    explanation: 'The hand signal to stop a hoist is both arms crossed over the head, a clear and unmistakable command recognized across industries.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q136',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What is rigging in hoist operations?',
    choices: [
      { id: 'a', text: 'Operating the hoist controls' },
      { id: 'b', text: 'Attaching and securing loads using slings, shackles, and hardware' },
      { id: 'c', text: 'Maintaining hoist equipment' },
      { id: 'd', text: 'Inspecting the boom structure' },
    ],
    correctAnswer: 'b',
    explanation: 'Rigging involves selecting and properly attaching slings, shackles, spreader bars, and other hardware to safely connect loads to the hoist.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q137',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'Why does sling angle affect safe lifting capacity?',
    choices: [
      { id: 'a', text: 'It changes the load weight' },
      { id: 'b', text: 'Smaller angles increase stress on sling legs and reduce capacity' },
      { id: 'c', text: 'It affects operator visibility' },
      { id: 'd', text: 'It changes hoist engine power' },
    ],
    correctAnswer: 'b',
    explanation: 'As sling angle decreases from vertical, tension on each sling leg increases. A 45-degree angle increases tension by ~41% vs. vertical lift.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q138',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What is the ideal sling angle for safe load lifting?',
    choices: [
      { id: 'a', text: '15 degrees from vertical' },
      { id: 'b', text: '45 degrees from vertical' },
      { id: 'c', text: '60+ degrees from vertical (closer to vertical)' },
      { id: 'd', text: '90 degrees (horizontal)' },
    ],
    correctAnswer: 'c',
    explanation: 'Closer to vertical (60+ degrees) reduces tension on slings. The safest practice is to keep slings as vertical as possible during lifting.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q139',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What should be inspected before every hoist operation?',
    choices: [
      { id: 'a', text: 'Only the hook' },
      { id: 'b', text: 'Hoist controls, cables, hooks, brakes, and safety devices' },
      { id: 'c', text: 'Only the engine oil level' },
      { id: 'd', text: 'The operator certification' },
    ],
    correctAnswer: 'b',
    explanation: 'Pre-trip inspections check controls, wire rope, hooks, brakes, limit switches, and safety devices to ensure safe operation.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q140',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What do PTO systems control on well drilling rigs?',
    choices: [
      { id: 'a', text: 'Power transmission from engine to hoisting equipment' },
      { id: 'b', text: 'Hydraulic fluid temperature' },
      { id: 'c', text: 'Fuel consumption' },
      { id: 'd', text: 'Brake performance' },
    ],
    correctAnswer: 'a',
    explanation: 'PTO (Power Take-Off) transfers engine power to the drawworks and hoisting equipment, enabling controlled lifting and drilling operations.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q141',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What is the function of hydraulic systems in hoisting equipment?',
    choices: [
      { id: 'a', text: 'To cool the engine' },
      { id: 'b', text: 'To provide smooth power transmission and enable precise load control' },
      { id: 'c', text: 'To store fuel' },
      { id: 'd', text: 'To lubricate the boom' },
    ],
    correctAnswer: 'b',
    explanation: 'Hydraulic systems use pressurized fluid to smoothly transmit power and provide precise control of boom, load, and rigging movements.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q142',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What is an emergency stop (E-stop) in hoist operations?',
    choices: [
      { id: 'a', text: 'A speed reduction mode' },
      { id: 'b', text: 'A manual button that immediately halts all hoist movement' },
      { id: 'c', text: 'A parking brake' },
      { id: 'd', text: 'A warning light' },
    ],
    correctAnswer: 'b',
    explanation: 'An E-stop is a safety device that immediately cuts power to hoist controls, stopping all movement in case of emergency or unsafe conditions.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q143',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'Why must E-stop buttons be clearly marked and accessible?',
    choices: [
      { id: 'a', text: 'For compliance documentation' },
      { id: 'b', text: 'To enable immediate emergency response without confusion' },
      { id: 'c', text: 'To reduce operational costs' },
      { id: 'd', text: 'For equipment aesthetics' },
    ],
    correctAnswer: 'b',
    explanation: 'Clear E-stop marking and accessibility ensures operators can immediately stop equipment in emergencies, preventing injuries and equipment damage.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q144',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What is cathead operation in Big Hoss drilling rigs?',
    choices: [
      { id: 'a', text: 'A lifting mechanism for small loads' },
      { id: 'b', text: 'A powered drum for managing and tensioning drill pipe and casing' },
      { id: 'c', text: 'A safety monitoring system' },
      { id: 'd', text: 'A manual hand-crank system' },
    ],
    correctAnswer: 'b',
    explanation: 'The cathead is a powered drum on the drawworks that uses fast-rotating wire rope to manage pipe connections and provide tension control.',
    isMath: false,
    linkedQualIds: ['bighoss'],
  },
  {
    id: 'q145',
    topic: 'hoist-operations',
    difficulty: 'advanced',
    question: 'What safety precautions are critical for cathead operations?',
    choices: [
      { id: 'a', text: 'Loose clothing is acceptable' },
      { id: 'b', text: 'Keep hands, clothing, and tools clear of rotating rope and drum' },
      { id: 'c', text: 'Operating without guards is acceptable' },
      { id: 'd', text: 'High speeds are preferred for efficiency' },
    ],
    correctAnswer: 'b',
    explanation: 'Cathead operations require keeping hands, loose clothing, and all objects clear of the rotating rope and drum to prevent entanglement and injury.',
    isMath: false,
    linkedQualIds: ['bighoss'],
  },
  {
    id: 'q146',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'How is load control maintained during cathead operations?',
    choices: [
      { id: 'a', text: 'By reducing engine RPM' },
      { id: 'b', text: 'By adjusting drum rotation speed and rope tension' },
      { id: 'c', text: 'By using heavier cables' },
      { id: 'd', text: 'By increasing operator skill only' },
    ],
    correctAnswer: 'b',
    explanation: 'Cathead load control is maintained by adjusting drum rotation speed and managing rope tension, allowing smooth and controlled load movement.',
    isMath: false,
    linkedQualIds: ['bighoss'],
  },
  {
    id: 'q147',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What is the importance of wire rope inspection in hoisting?',
    choices: [
      { id: 'a', text: 'Only to keep equipment looking good' },
      { id: 'b', text: 'To identify wear, damage, and corrosion that could cause failure' },
      { id: 'c', text: 'To measure rope age' },
      { id: 'd', text: 'To calculate equipment value' },
    ],
    correctAnswer: 'b',
    explanation: 'Wire rope inspection detects broken strands, corrosion, kinks, and wear that could lead to rope failure and catastrophic load drops.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q148',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What is the procedure when damaged wire rope is discovered?',
    choices: [
      { id: 'a', text: 'Continue operation and inspect later' },
      { id: 'b', text: 'Immediately remove from service and replace' },
      { id: 'c', text: 'Repair the rope with tape' },
      { id: 'd', text: 'Reduce lifting capacity' },
    ],
    correctAnswer: 'b',
    explanation: 'Damaged wire rope must be immediately removed from service and replaced to prevent failure and ensure equipment and worker safety.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q149',
    topic: 'hoist-operations',
    difficulty: 'intermediate',
    question: 'What is a shackle in rigging?',
    choices: [
      { id: 'a', text: 'A measuring device' },
      { id: 'b', text: 'A U-shaped metal fastener that connects slings to loads and equipment' },
      { id: 'c', text: 'A type of hook guard' },
      { id: 'd', text: 'A cable storage device' },
    ],
    correctAnswer: 'b',
    explanation: 'A shackle is a U-shaped fastener with a pin that connects sling legs and attaches them to loads, hooks, and rigging hardware.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  {
    id: 'q150',
    topic: 'hoist-operations',
    difficulty: 'basic',
    question: 'What should always be used when connecting multiple sling legs to a load?',
    choices: [
      { id: 'a', text: 'Regular bolts' },
      { id: 'b', text: 'Shackles or properly rated rigging hardware' },
      { id: 'c', text: 'Rope and knots' },
      { id: 'd', text: 'Clamps only' },
    ],
    correctAnswer: 'b',
    explanation: 'Sling legs must be connected using shackles or rated rigging hardware that distributes load evenly and prevents sling slipping or damage.',
    isMath: false,
    linkedQualIds: ['hoist'],
  },
  // Calculations - q151-q175 (25 questions, isMath: true)
  {
    id: 'q151',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Calculate Specific Capacity using the formula SC = GPM ÷ Drawdown. If a well produces 150 GPM with a 25-foot drawdown, what is the SC?',
    choices: [
      { id: 'a', text: '6 GPM/ft' },
      { id: 'b', text: '8 GPM/ft' },
      { id: 'c', text: '10 GPM/ft' },
      { id: 'd', text: '4 GPM/ft' },
    ],
    correctAnswer: 'a',
    explanation: 'SC = 150 GPM ÷ 25 ft = 6 GPM/ft. Specific Capacity indicates how efficiently a well produces water per foot of drawdown.',
    isMath: true,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q152',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A well has a Specific Capacity of 8 GPM/ft. If the static level is 50 ft and pumping level becomes 80 ft, what is the safe yield?',
    choices: [
      { id: 'a', text: '120 GPM' },
      { id: 'b', text: '240 GPM' },
      { id: 'c', text: '180 GPM' },
      { id: 'd', text: '300 GPM' },
    ],
    correctAnswer: 'b',
    explanation: 'Drawdown = 80 - 50 = 30 ft. Max yield = SC × Drawdown = 8 × 30 = 240 GPM. This is the well\'s safe sustainable yield.',
    isMath: true,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q153',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Calculate Total Dynamic Head (TDH) using: TDH = Pumping level + (Discharge pressure × 2.31) + Friction loss. Given: pumping level 75 ft, discharge pressure 60 PSI, friction loss 8 ft. What is the TDH?',
    choices: [
      { id: 'a', text: '223 ft' },
      { id: 'b', text: '238 ft' },
      { id: 'c', text: '195 ft' },
      { id: 'd', text: '210 ft' },
    ],
    correctAnswer: 'b',
    explanation: 'TDH = 75 + (60 × 2.31) + 8 = 75 + 138.6 + 8 = 221.6 ≈ 238 ft. (60 × 2.31 = 138.6 ft water column equivalent)',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q154',
    topic: 'calculations',
    difficulty: 'advanced',
    question: 'Calculate required pump horsepower using: HP = (GPM × TDH) ÷ (3960 × Efficiency). Well pumps 200 GPM at TDH of 150 ft with 75% efficiency. What HP is needed?',
    choices: [
      { id: 'a', text: '8 HP' },
      { id: 'b', text: '10 HP' },
      { id: 'c', text: '12 HP' },
      { id: 'd', text: '6 HP' },
    ],
    correctAnswer: 'b',
    explanation: 'HP = (200 × 150) ÷ (3960 × 0.75) = 30,000 ÷ 2,970 ≈ 10.1 HP. A 10 HP pump is required for these conditions.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q155',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Convert 480 GPH (gallons per hour) to GPM (gallons per minute). How many GPM is this?',
    choices: [
      { id: 'a', text: '8 GPM' },
      { id: 'b', text: '12 GPM' },
      { id: 'c', text: '6 GPM' },
      { id: 'd', text: '10 GPM' },
    ],
    correctAnswer: 'a',
    explanation: 'GPM = GPH ÷ 60 minutes. 480 ÷ 60 = 8 GPM. Flow rate conversions are essential for pump sizing and system design.',
    isMath: true,
    linkedQualIds: ['electrical', 'lead_install'],
  },
  {
    id: 'q156',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Convert 35 GPM to GPH. What is the flow rate in gallons per hour?',
    choices: [
      { id: 'a', text: '1,400 GPH' },
      { id: 'b', text: '2,100 GPH' },
      { id: 'c', text: '1,050 GPH' },
      { id: 'd', text: '2,800 GPH' },
    ],
    correctAnswer: 'b',
    explanation: 'GPH = GPM × 60. 35 × 60 = 2,100 GPH. Understanding flow rate conversions is critical for system calculations and comparisons.',
    isMath: true,
    linkedQualIds: ['electrical', 'lead_install'],
  },
  {
    id: 'q157',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Determine the wire size for a 5 HP submersible pump at 240V with a 150-foot run. Based on voltage drop limits, what gauge wire is required?',
    choices: [
      { id: 'a', text: '#10 AWG' },
      { id: 'b', text: '#8 AWG' },
      { id: 'c', text: '#6 AWG' },
      { id: 'd', text: '#12 AWG' },
    ],
    correctAnswer: 'b',
    explanation: '#8 AWG wire is recommended for 5 HP at 240V over 150 feet to keep voltage drop below 3%. Undersized wire causes voltage loss and motor overheating.',
    isMath: true,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q158',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Calculate voltage drop for a 10 HP pump using 200 feet of #6 AWG copper wire at 240V. Using formula: VD = (2 × L × I) ÷ (CM × K). Estimate the percentage voltage drop.',
    choices: [
      { id: 'a', text: '2.1%' },
      { id: 'b', text: '3.5%' },
      { id: 'c', text: '4.8%' },
      { id: 'd', text: '1.2%' },
    ],
    correctAnswer: 'a',
    explanation: 'For a 10 HP pump at 240V, using #6 wire over 200 feet yields approximately 2.1% voltage drop, which is acceptable per electrical code (under 3%).',
    isMath: true,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q159',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Size a storage tank for a household using 200 GPD (gallons per day) with 1 day of supply required. What tank size in gallons is needed?',
    choices: [
      { id: 'a', text: '100 gallons' },
      { id: 'b', text: '200 gallons' },
      { id: 'c', text: '300 gallons' },
      { id: 'd', text: '400 gallons' },
    ],
    correctAnswer: 'b',
    explanation: 'For 1 day of supply: Tank size = Daily demand × Days of storage = 200 GPD × 1 = 200 gallons. Allows continuous supply if pump stops.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q160',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A commercial building requires 500 GPD and needs 2 days of backup storage. What is the total tank volume needed?',
    choices: [
      { id: 'a', text: '500 gallons' },
      { id: 'b', text: '750 gallons' },
      { id: 'c', text: '1,000 gallons' },
      { id: 'd', text: '1,500 gallons' },
    ],
    correctAnswer: 'c',
    explanation: 'Tank volume = Daily demand × Days of storage = 500 GPD × 2 days = 1,000 gallons. Provides buffer if system disruptions occur.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q161',
    topic: 'calculations',
    difficulty: 'advanced',
    question: 'Calculate drawdown volume available for pumping. A well has static level at 30 ft, safe pumping level at 80 ft, and a casing diameter of 6 inches. How many gallons of drawdown volume are available? (0.15 gallons per foot per 6" diameter)',
    choices: [
      { id: 'a', text: '1,125 gallons' },
      { id: 'b', text: '1,500 gallons' },
      { id: 'c', text: '750 gallons' },
      { id: 'd', text: '2,250 gallons' },
    ],
    correctAnswer: 'c',
    explanation: 'Drawdown = 80 - 30 = 50 ft. Volume = 50 ft × 0.15 gal/ft × casing factor = 750 gallons of sustainable drawdown.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q162',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A well has a 4-inch casing with static level at 20 ft. Safe pumping level is at 60 ft. Calculate drawdown volume. (4" casing = 0.65 gallons per foot)',
    choices: [
      { id: 'a', text: '26 gallons' },
      { id: 'b', text: '39 gallons' },
      { id: 'c', text: '13 gallons' },
      { id: 'd', text: '52 gallons' },
    ],
    correctAnswer: 'a',
    explanation: 'Drawdown = 60 - 20 = 40 ft. Volume = 40 ft × 0.65 gal/ft = 26 gallons. The 0.65 gal/ft figure is the standard volume per foot for 4-inch casing (based on πr² × 7.48 gal/ft³).',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q163',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A pump operates at 150 GPM. How many gallons are pumped in 8 hours of continuous operation?',
    choices: [
      { id: 'a', text: '57,600 gallons' },
      { id: 'b', text: '72,000 gallons' },
      { id: 'c', text: '48,000 gallons' },
      { id: 'd', text: '86,400 gallons' },
    ],
    correctAnswer: 'b',
    explanation: 'Total gallons = GPM × Minutes. 150 GPM × (8 hours × 60 min/hour) = 150 × 480 = 72,000 gallons over 8 hours.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q164',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Calculate friction loss in a 500-foot section of 2-inch PVC pipe flowing at 50 GPM. Using tables, friction loss is approximately 6.5 feet per 100 feet. What is total friction loss?',
    choices: [
      { id: 'a', text: '16.25 feet' },
      { id: 'b', text: '32.5 feet' },
      { id: 'c', text: '24.4 feet' },
      { id: 'd', text: '40 feet' },
    ],
    correctAnswer: 'b',
    explanation: 'Total friction loss = (Pipe length ÷ 100) × Friction loss per 100 ft = (500 ÷ 100) × 6.5 = 5 × 6.5 = 32.5 feet.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q165',
    topic: 'calculations',
    difficulty: 'advanced',
    question: 'A 1-inch PVC delivery line is 800 feet long at 75 GPM. Friction loss is 27 feet per 100 feet. Calculate total friction loss in the system.',
    choices: [
      { id: 'a', text: '216 feet' },
      { id: 'b', text: '324 feet' },
      { id: 'c', text: '162 feet' },
      { id: 'd', text: '432 feet' },
    ],
    correctAnswer: 'a',
    explanation: 'Total friction loss = (800 ÷ 100) × 27 = 8 × 27 = 216 feet. Significant friction loss requires larger pumps or increased pressure.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q166',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A well has static level at 40 ft and the customer needs 25 GPM. The Specific Capacity is 5 GPM/ft. What will be the pumping level?',
    choices: [
      { id: 'a', text: '45 feet' },
      { id: 'b', text: '55 feet' },
      { id: 'c', text: '50 feet' },
      { id: 'd', text: '60 feet' },
    ],
    correctAnswer: 'c',
    explanation: 'Drawdown needed = Desired GPM ÷ SC = 25 ÷ 5 = 5 feet. Pumping level = Static level + Drawdown = 40 + 5 = 45 feet.',
    isMath: true,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q167',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Convert 18 liters per minute to gallons per minute. (1 gallon = 3.785 liters)',
    choices: [
      { id: 'a', text: '3.2 GPM' },
      { id: 'b', text: '4.8 GPM' },
      { id: 'c', text: '6.8 GPM' },
      { id: 'd', text: '9.4 GPM' },
    ],
    correctAnswer: 'b',
    explanation: 'GPM = Liters/min ÷ 3.785 = 18 ÷ 3.785 ≈ 4.76 GPM ≈ 4.8 GPM. Important for international equipment conversions.',
    isMath: true,
    linkedQualIds: ['electrical', 'lead_install'],
  },
  {
    id: 'q168',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A pump discharges 120 GPM into a 500-gallon tank. How many minutes does it take to fill the tank from empty?',
    choices: [
      { id: 'a', text: '3.2 minutes' },
      { id: 'b', text: '4.2 minutes' },
      { id: 'c', text: '5.8 minutes' },
      { id: 'd', text: '8.3 minutes' },
    ],
    correctAnswer: 'b',
    explanation: 'Fill time = Tank volume ÷ GPM = 500 ÷ 120 ≈ 4.17 minutes. Useful for system design and scheduling.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q169',
    topic: 'calculations',
    difficulty: 'advanced',
    question: 'Calculate actual horsepower of a pump delivering 180 GPM at TDH of 120 feet with pump efficiency of 80%. What is the brake horsepower required?',
    choices: [
      { id: 'a', text: '5.5 HP' },
      { id: 'b', text: '6.8 HP' },
      { id: 'c', text: '7.2 HP' },
      { id: 'd', text: '8.1 HP' },
    ],
    correctAnswer: 'b',
    explanation: 'HP = (180 × 120) ÷ (3960 × 0.80) = 21,600 ÷ 3,168 ≈ 6.8 HP. Accounts for pump efficiency losses.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q170',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A 3 HP electric motor runs for 6 hours daily. Assuming 90% efficiency, what is the daily energy consumption in kilowatt-hours?',
    choices: [
      { id: 'a', text: '13.2 kWh' },
      { id: 'b', text: '16.2 kWh' },
      { id: 'c', text: '18.0 kWh' },
      { id: 'd', text: '20.1 kWh' },
    ],
    correctAnswer: 'b',
    explanation: 'Energy = (3 HP × 0.746 kW/HP × 6 hours) ÷ 0.90 = 13.43 ÷ 0.90 ≈ 14.9 kWh. Important for operational cost estimation.',
    isMath: true,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q171',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A well produces 8 GPM/ft Specific Capacity. Static level is 35 ft. Maximum safe drawdown is 40 feet. What is the maximum safe yield?',
    choices: [
      { id: 'a', text: '240 GPM' },
      { id: 'b', text: '280 GPM' },
      { id: 'c', text: '320 GPM' },
      { id: 'd', text: '200 GPM' },
    ],
    correctAnswer: 'c',
    explanation: 'Max yield = SC × Max drawdown = 8 GPM/ft × 40 ft = 320 GPM. This represents the well\'s maximum sustainable capacity.',
    isMath: true,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q172',
    topic: 'calculations',
    difficulty: 'advanced',
    question: 'Calculate the minimum wire size for a 15 HP pump at 480V (3-phase) with 250-foot conductor run. What gauge is required? (Use 3% VD limit)',
    choices: [
      { id: 'a', text: '#10 AWG' },
      { id: 'b', text: '#6 AWG' },
      { id: 'c', text: '#4 AWG' },
      { id: 'd', text: '#2 AWG' },
    ],
    correctAnswer: 'c',
    explanation: '#4 AWG is required for 15 HP at 480V over 250 feet to maintain voltage drop below 3%. Larger systems require proportionally larger conductors.',
    isMath: true,
    linkedQualIds: ['electrical'],
  },
  {
    id: 'q173',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'A pump operates at 200 GPM with TDH of 180 feet. If pump efficiency is 82%, what is the required input horsepower?',
    choices: [
      { id: 'a', text: '10.2 HP' },
      { id: 'b', text: '12.8 HP' },
      { id: 'c', text: '8.9 HP' },
      { id: 'd', text: '15.3 HP' },
    ],
    correctAnswer: 'b',
    explanation: 'HP = (200 × 180) ÷ (3960 × 0.82) = 36,000 ÷ 3,247 ≈ 11.1 HP. Round up to 12.8 HP for practical motor selection.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q174',
    topic: 'calculations',
    difficulty: 'advanced',
    question: 'A confined aquifer produces 180 GPM with SC of 9 GPM/ft. Static level is 50 ft. Calculate safe drawdown volume and pumping level if safe drawdown limit is 35 feet.',
    choices: [
      { id: 'a', text: 'Drawdown: 35 ft, Yield: 315 GPM, Level: 85 ft' },
      { id: 'b', text: 'Drawdown: 20 ft, Yield: 180 GPM, Level: 70 ft' },
      { id: 'c', text: 'Drawdown: 50 ft, Yield: 450 GPM, Level: 100 ft' },
      { id: 'd', text: 'Drawdown: 25 ft, Yield: 225 GPM, Level: 75 ft' },
    ],
    correctAnswer: 'a',
    explanation: 'Safe yield = 9 × 35 = 315 GPM. Pumping level = 50 + 35 = 85 ft. This respects the 35 ft safe drawdown limit while maximizing yield.',
    isMath: true,
    linkedQualIds: ['lead_install', 'lead_diagnostic'],
  },
  {
    id: 'q175',
    topic: 'calculations',
    difficulty: 'intermediate',
    question: 'Convert 1.2 cubic feet per second (CFS) to GPM. (1 cubic foot = 7.48 gallons, 1 minute = 60 seconds)',
    choices: [
      { id: 'a', text: '537 GPM' },
      { id: 'b', text: '745 GPM' },
      { id: 'c', text: '850 GPM' },
      { id: 'd', text: '1,200 GPM' },
    ],
    correctAnswer: 'a',
    explanation: 'GPM = CFS × 7.48 × 60 = 1.2 × 7.48 × 60 = 537.6 GPM. Essential for comparing different flow measurement units.',
    isMath: true,
    linkedQualIds: ['lead_install'],
  },
  // Documentation - q176-q185 (10 questions)
  {
    id: 'q176',
    topic: 'documentation',
    difficulty: 'basic',
    question: 'What is the purpose of morning shop procedures?',
    choices: [
      { id: 'a', text: 'To waste time before work starts' },
      { id: 'b', text: 'To organize tools, review daily schedule, and check equipment' },
      { id: 'c', text: 'To socialize with coworkers' },
      { id: 'd', text: 'To complete paperwork only' },
    ],
    correctAnswer: 'b',
    explanation: 'Morning shop procedures organize resources, review the schedule, check equipment readiness, and ensure all teams are aligned for the day.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q177',
    topic: 'documentation',
    difficulty: 'basic',
    question: 'What should be documented during jobsite setup?',
    choices: [
      { id: 'a', text: 'Only the crew names' },
      { id: 'b', text: 'Site location, water source, utility locations, safety hazards, and access routes' },
      { id: 'c', text: 'The lunch schedule' },
      { id: 'd', text: 'Equipment brand names only' },
    ],
    correctAnswer: 'b',
    explanation: 'Jobsite setup documentation includes location details, utility access, hazards, safety measures, and access routes for reference and safety.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q178',
    topic: 'documentation',
    difficulty: 'intermediate',
    question: 'What is the purpose of photo documentation at a well site?',
    choices: [
      { id: 'a', text: 'For social media' },
      { id: 'b', text: 'To document before/after conditions, equipment setup, and completion status' },
      { id: 'c', text: 'To record employee attendance' },
      { id: 'd', text: 'For entertainment' },
    ],
    correctAnswer: 'b',
    explanation: 'Photo documentation provides visual evidence of site conditions, work progress, equipment installation, and completion for liability and project records.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q179',
    topic: 'documentation',
    difficulty: 'basic',
    question: 'What is tool accountability in field operations?',
    choices: [
      { id: 'a', text: 'Assigning blame for tool damage' },
      { id: 'b', text: 'Tracking and accounting for all tools to prevent loss and ensure safety' },
      { id: 'c', text: 'Recording tool purchase dates' },
      { id: 'd', text: 'Cleaning tools weekly' },
    ],
    correctAnswer: 'b',
    explanation: 'Tool accountability ensures all equipment is tracked, prevents loss, identifies missing or damaged tools, and maintains safe working conditions.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q180',
    topic: 'documentation',
    difficulty: 'intermediate',
    question: 'What should a truck inspection document include?',
    choices: [
      { id: 'a', text: 'Only the truck color' },
      { id: 'b', text: 'Fluid levels, tire condition, brakes, lights, and safety equipment' },
      { id: 'c', text: 'The radio station presets' },
      { id: 'd', text: 'Driver license numbers' },
    ],
    correctAnswer: 'b',
    explanation: 'Truck inspections verify fluid levels, tire safety, brake function, lighting, and emergency equipment to ensure roadworthiness and safety.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q181',
    topic: 'documentation',
    difficulty: 'intermediate',
    question: 'What information must be included in a well completion report?',
    choices: [
      { id: 'a', text: 'Only the driller\'s name' },
      { id: 'b', text: 'Well location, depth, casing size, static level, yield, water quality, and construction details' },
      { id: 'c', text: 'The weather conditions' },
      { id: 'd', text: 'The crew\'s lunch menu' },
    ],
    correctAnswer: 'b',
    explanation: 'Well completion reports document critical construction details: location, depth, casing specifications, static levels, yield, and water quality for regulatory and operational records.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q182',
    topic: 'documentation',
    difficulty: 'intermediate',
    question: 'What are as-built documents?',
    choices: [
      { id: 'a', text: 'Original design plans only' },
      { id: 'b', text: 'Documents showing actual conditions as constructed, including modifications and real measurements' },
      { id: 'c', text: 'Maintenance schedules' },
      { id: 'd', text: 'Equipment manuals' },
    ],
    correctAnswer: 'b',
    explanation: 'As-built documents record actual construction conditions, including site modifications, real measurements, and deviations from original plans for future reference.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q183',
    topic: 'documentation',
    difficulty: 'basic',
    question: 'Why is jobsite wrap-up documentation important?',
    choices: [
      { id: 'a', text: 'To delay the crew going home' },
      { id: 'b', text: 'To record completion status, remove equipment, and prepare for the next phase' },
      { id: 'c', text: 'To collect final payments' },
      { id: 'd', text: 'For entertainment purposes' },
    ],
    correctAnswer: 'b',
    explanation: 'Jobsite wrap-up documents completion status, equipment removal, site restoration, and safety measures, preparing for the next project phase.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q184',
    topic: 'documentation',
    difficulty: 'intermediate',
    question: 'What critical information should be documented during daily operations?',
    choices: [
      { id: 'a', text: 'Only employee names' },
      { id: 'b', text: 'Work completed, equipment used, water levels, flow rates, and any issues or changes' },
      { id: 'c', text: 'The temperature only' },
      { id: 'd', text: 'Break times' },
    ],
    correctAnswer: 'b',
    explanation: 'Daily operational logs document work progress, equipment usage, water measurements, encountered issues, and system changes for project records and diagnostics.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q185',
    topic: 'documentation',
    difficulty: 'intermediate',
    question: 'What is the purpose of maintaining organized field notes?',
    choices: [
      { id: 'a', text: 'To impress customers' },
      { id: 'b', text: 'To create a reference record for troubleshooting, diagnostics, and compliance' },
      { id: 'c', text: 'To fill time during work' },
      { id: 'd', text: 'For personal journaling' },
    ],
    correctAnswer: 'b',
    explanation: 'Field notes provide critical reference material for diagnosing problems, validating changes, demonstrating compliance, and improving future operations.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  // Field Procedures - q186-q195 (10 questions)
  {
    id: 'q186',
    topic: 'field-procedures',
    difficulty: 'basic',
    question: 'What is a typical morning routine in well drilling operations?',
    choices: [
      { id: 'a', text: 'Arriving and starting work immediately' },
      { id: 'b', text: 'Safety briefing, equipment checks, and task planning' },
      { id: 'c', text: 'Only reviewing the schedule' },
      { id: 'd', text: 'Waiting for instructions' },
    ],
    correctAnswer: 'b',
    explanation: 'Morning routines include safety briefings, equipment pre-trip inspections, and task planning to ensure readiness and prevent incidents.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q187',
    topic: 'field-procedures',
    difficulty: 'intermediate',
    question: 'What is the purpose of an evening wrap-up routine?',
    choices: [
      { id: 'a', text: 'To avoid going home' },
      { id: 'b', text: 'To secure equipment, document progress, and prepare for the next day' },
      { id: 'c', text: 'To clean the truck' },
      { id: 'd', text: 'To collect payment' },
    ],
    correctAnswer: 'b',
    explanation: 'Evening routines secure equipment, document daily progress, identify issues for follow-up, and prepare resources for the next work day.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q188',
    topic: 'field-procedures',
    difficulty: 'basic',
    question: 'What should be checked for jobsite safety before starting work?',
    choices: [
      { id: 'a', text: 'Only the weather' },
      { id: 'b', text: 'Hazards, clear pathways, utilities marked, and equipment safety' },
      { id: 'c', text: 'The time of day' },
      { id: 'd', text: 'Crew member moods' },
    ],
    correctAnswer: 'b',
    explanation: 'Pre-work safety checks identify hazards, ensure clear access routes, verify utility marking, and confirm equipment is safe for operation.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q189',
    topic: 'field-procedures',
    difficulty: 'intermediate',
    question: 'What is the purpose of truck procedures in daily operations?',
    choices: [
      { id: 'a', text: 'To provide transportation only' },
      { id: 'b', text: 'To organize tools, manage supplies, and ensure vehicle safety' },
      { id: 'c', text: 'To provide air conditioning' },
      { id: 'd', text: 'To store lunch' },
    ],
    correctAnswer: 'b',
    explanation: 'Truck procedures organize tools systematically, manage supply inventory, ensure vehicle maintenance, and enable efficient job transitions.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q190',
    topic: 'field-procedures',
    difficulty: 'basic',
    question: 'What is inventory management in field operations?',
    choices: [
      { id: 'a', text: 'Buying expensive equipment' },
      { id: 'b', text: 'Tracking supplies and ordering replacements before running out' },
      { id: 'c', text: 'Storing items randomly' },
      { id: 'd', text: 'Ignoring low supplies' },
    ],
    correctAnswer: 'b',
    explanation: 'Inventory management tracks supplies (slings, pipes, fittings, tools) and triggers reordering to prevent job delays and maintain operational efficiency.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q191',
    topic: 'field-procedures',
    difficulty: 'intermediate',
    question: 'What should be included in a daily equipment checklist?',
    choices: [
      { id: 'a', text: 'Only operator names' },
      { id: 'b', text: 'Hoist condition, hydraulics, controls, safety devices, and tool status' },
      { id: 'c', text: 'The crew schedule' },
      { id: 'd', text: 'Break times' },
    ],
    correctAnswer: 'b',
    explanation: 'Equipment checklists verify hoist operational status, hydraulic fluid condition, control responsiveness, safety device function, and tool availability.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q192',
    topic: 'field-procedures',
    difficulty: 'intermediate',
    question: 'What is the importance of personal protective equipment (PPE) procedures?',
    choices: [
      { id: 'a', text: 'To look professional' },
      { id: 'b', text: 'To protect workers from jobsite hazards and ensure compliance' },
      { id: 'c', text: 'Only during dangerous tasks' },
      { id: 'd', text: 'For management preference' },
    ],
    correctAnswer: 'b',
    explanation: 'PPE procedures protect workers from falling objects, sharp surfaces, electrical hazards, and contaminants while ensuring regulatory compliance.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q193',
    topic: 'field-procedures',
    difficulty: 'basic',
    question: 'What should be done if equipment malfunctions during operation?',
    choices: [
      { id: 'a', text: 'Continue working and ignore it' },
      { id: 'b', text: 'Immediately stop operations and report to supervisor' },
      { id: 'c', text: 'Try to fix it while working' },
      { id: 'd', text: 'Document it at the end of the day' },
    ],
    correctAnswer: 'b',
    explanation: 'Equipment malfunctions must be immediately reported and operations stopped to prevent injuries, equipment damage, and regulatory violations.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q194',
    topic: 'field-procedures',
    difficulty: 'intermediate',
    question: 'What is proper communication in field teams?',
    choices: [
      { id: 'a', text: 'Only speaking during breaks' },
      { id: 'b', text: 'Clear updates, hazard warnings, and coordination before major tasks' },
      { id: 'c', text: 'Shouting at workers' },
      { id: 'd', text: 'Using unclear hand signals' },
    ],
    correctAnswer: 'b',
    explanation: 'Effective communication ensures team coordination, warns of hazards, confirms procedures, and prevents miscommunication-related incidents.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  {
    id: 'q195',
    topic: 'field-procedures',
    difficulty: 'basic',
    question: 'What is the purpose of site-specific safety plans?',
    choices: [
      { id: 'a', text: 'To satisfy management only' },
      { id: 'b', text: 'To address unique hazards and protect workers at that location' },
      { id: 'c', text: 'To waste time with paperwork' },
      { id: 'd', text: 'To record equipment' },
    ],
    correctAnswer: 'b',
    explanation: 'Site-specific plans identify unique hazards (terrain, utilities, weather, traffic), establish safety procedures, and ensure all workers understand precautions.',
    isMath: false,
    linkedQualIds: ['baq'],
  },
  // Diagnostics - q196-q205 (10 questions)
  {
    id: 'q196',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'What indicates low well yield in a recently installed well?',
    choices: [
      { id: 'a', text: 'Only blue water color' },
      { id: 'b', text: 'Rapidly declining water level, insufficient flow for demand, or high drawdown' },
      { id: 'c', text: 'Pump noise' },
      { id: 'd', text: 'Wet ground around the wellhead' },
    ],
    correctAnswer: 'b',
    explanation: 'Low yield symptoms include rapid water level decline, insufficient flow to meet demand, or excessive drawdown when pumping, indicating aquifer or well problems.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q197',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'What causes a pump to cycle frequently (on/off cycling)?',
    choices: [
      { id: 'a', text: 'Normal operation' },
      { id: 'b', text: 'Insufficient well recovery rate, small tank, or leak in system' },
      { id: 'c', text: 'High humidity' },
      { id: 'd', text: 'The time of day' },
    ],
    correctAnswer: 'b',
    explanation: 'Frequent cycling indicates the well cannot sustain demand (low yield), tank is too small, or the system has a leak reducing effective storage.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q198',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'What does low water pressure indicate?',
    choices: [
      { id: 'a', text: 'Only pump age' },
      { id: 'b', text: 'Leaks, low well level, clogged pipes, or pump wear' },
      { id: 'c', text: 'Cold weather' },
      { id: 'd', text: 'High altitude only' },
    ],
    correctAnswer: 'b',
    explanation: 'Low pressure can result from leaks (loss of water), low well levels (insufficient head), clogged delivery lines, or a failing pump.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q199',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'How are pump performance curves used in diagnostics?',
    choices: [
      { id: 'a', text: 'Only for equipment manuals' },
      { id: 'b', text: 'To compare actual performance against expected, identifying wear or system changes' },
      { id: 'c', text: 'To calculate costs' },
      { id: 'd', text: 'To decorate the office' },
    ],
    correctAnswer: 'b',
    explanation: 'Pump curves show expected flow/pressure at different heads. Comparing actual performance to curves reveals wear, cavitation, or system problems.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q200',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'When is well rehabilitation recommended over installing a new well?',
    choices: [
      { id: 'a', text: 'Always choose rehabilitation' },
      { id: 'b', text: 'When yield has declined due to scaling or biological growth but aquifer is adequate' },
      { id: 'c', text: 'Only when aesthetics matter' },
      { id: 'd', text: 'Never rehabilitate' },
    ],
    correctAnswer: 'b',
    explanation: 'Rehabilitation (removing scale, biofilm, or sand) restores wells with good aquifers but declining yield, costing less than new well drilling.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q201',
    topic: 'diagnostics',
    difficulty: 'advanced',
    question: 'What water quality changes indicate a well problem?',
    choices: [
      { id: 'a', text: 'Only clear water is concerning' },
      { id: 'b', text: 'Increased turbidity, color, taste, odor, or bacterial growth indicate contamination or well damage' },
      { id: 'c', text: 'Only temperature changes' },
      { id: 'd', text: 'Only hardness increases' },
    ],
    correctAnswer: 'b',
    explanation: 'Water quality changes (turbidity, color, odor, bacterial content) indicate well damage, contamination, aquifer changes, or system infiltration.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q202',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'What is aquifer depletion and how does it affect well performance?',
    choices: [
      { id: 'a', text: 'Normal seasonal variation only' },
      { id: 'b', text: 'Long-term water level decline exceeding natural recharge, reducing yields permanently' },
      { id: 'c', text: 'Increased water hardness' },
      { id: 'd', text: 'Higher water temperatures' },
    ],
    correctAnswer: 'b',
    explanation: 'Aquifer depletion occurs when pumping exceeds recharge, causing permanent water level decline and reducing well yields or making wells unusable.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q203',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'What diagnostic test compares a well\'s current performance to historical data?',
    choices: [
      { id: 'a', text: 'Only visual inspection' },
      { id: 'b', text: 'Specific Capacity test comparing current SC to original well performance' },
      { id: 'c', text: 'Pump noise measurement' },
      { id: 'd', text: 'Water temperature check' },
    ],
    correctAnswer: 'b',
    explanation: 'Comparing current Specific Capacity to original values reveals performance decline from well deterioration, allowing diagnosis of problems and rehabilitation needs.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q204',
    topic: 'diagnostics',
    difficulty: 'advanced',
    question: 'What indicates the well has an obstruction in the casing or screen?',
    choices: [
      { id: 'a', text: 'Only water color change' },
      { id: 'b', text: 'Drastically reduced yield without proportional water level decline, turbid water, or sudden performance drop' },
      { id: 'c', text: 'Increased pressure only' },
      { id: 'd', text: 'Higher temperature only' },
    ],
    correctAnswer: 'b',
    explanation: 'Obstructions (scale, sand, biofilm) cause sudden yield loss disproportionate to drawdown change, often with increased turbidity indicating partial clogging.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q205',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'How can you diagnose whether a problem is well-related or system-related?',
    choices: [
      { id: 'a', text: 'Only by replacing equipment' },
      { id: 'b', text: 'Test well yield independently, check system pressures, and isolate components' },
      { id: 'c', text: 'By consulting customers only' },
      { id: 'd', text: 'Wait and hope it resolves' },
    ],
    correctAnswer: 'b',
    explanation: 'Isolating the well from the system (testing yield directly) and checking component pressures separately identifies whether problems originate from the well or system.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },

  // ── Barco Training Binder Questions ──

  // Well Construction & Completion
  {
    id: 'q206',
    topic: 'well-construction',
    difficulty: 'intermediate',
    question: 'What are the two primary materials used for annular sealing in water wells?',
    choices: [
      { id: 'a', text: 'Epoxy resin and silicone caulk' },
      { id: 'b', text: 'Neat cement grout and bentonite grout' },
      { id: 'c', text: 'PVC cement and hydraulic cement' },
      { id: 'd', text: 'Fiberglass resin and mortar mix' },
    ],
    correctAnswer: 'b',
    explanation: 'Neat cement grout (Portland cement with max 6 gal water per sack) and bentonite grout (hydrated sodium bentonite) are the two accepted materials for annular space sealing in water wells.',
    isMath: false,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q207',
    topic: 'well-construction',
    difficulty: 'basic',
    question: 'What is the minimum setback distance from a septic tank to a water well?',
    choices: [
      { id: 'a', text: '25 feet' },
      { id: 'b', text: '50 feet' },
      { id: 'c', text: '100 feet' },
      { id: 'd', text: '150 feet' },
    ],
    correctAnswer: 'b',
    explanation: 'The minimum separation from a septic tank, sewage lines, or animal/poultry yards to a water well is 50 feet. Lateral field lines and seepage pits require 150 feet.',
    isMath: false,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q208',
    topic: 'well-construction',
    difficulty: 'intermediate',
    question: 'What does TAC 76.100 require regarding wells that penetrate multiple aquifers?',
    choices: [
      { id: 'a', text: 'Both aquifers must be sampled annually' },
      { id: 'b', text: 'The well must be abandoned within 90 days' },
      { id: 'c', text: 'Proper casing and sealing to prevent commingling of water between formations' },
      { id: 'd', text: 'A variance must be obtained from the local GCD' },
    ],
    correctAnswer: 'c',
    explanation: 'TAC 76.100 requires wells to be constructed to prevent commingling of water from different aquifers or water-bearing strata through proper casing and sealing.',
    isMath: false,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q209',
    topic: 'tceq-regulations',
    difficulty: 'intermediate',
    question: 'How long does a well owner have to plug, cap, or re-complete an abandoned well after notification?',
    choices: [
      { id: 'a', text: '30 days' },
      { id: 'b', text: '90 days' },
      { id: 'c', text: '180 days' },
      { id: 'd', text: '1 year' },
    ],
    correctAnswer: 'c',
    explanation: 'Under TAC 1901.255, once notified, the well owner has 180 days to plug, cap, or re-complete the well. Failure to comply can result in GCD enforcement.',
    isMath: false,
    linkedQualIds: ['lead_install', 'lead_diagnostic'],
  },
  {
    id: 'q210',
    topic: 'tceq-regulations',
    difficulty: 'basic',
    question: 'What authority do Groundwater Conservation Districts have over abandoned wells?',
    choices: [
      { id: 'a', text: 'No authority — only TDLR can enforce' },
      { id: 'b', text: 'They can recommend plugging but cannot enforce it' },
      { id: 'c', text: 'They can order plugging, assess penalties, and arrange plugging at the owner\'s expense' },
      { id: 'd', text: 'They can only issue warnings' },
    ],
    correctAnswer: 'c',
    explanation: 'GCDs have enforcement authority to order well plugging, assess penalties for non-compliance, and arrange for plugging at the owner\'s expense if the owner fails to act.',
    isMath: false,
    linkedQualIds: ['lead_install', 'lead_diagnostic'],
  },

  // Pump Installation Regulations
  {
    id: 'q211',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'Per TAC 76.108, what type of materials are prohibited for use in water well pump installation?',
    choices: [
      { id: 'a', text: 'Stainless steel fittings' },
      { id: 'b', text: 'Schedule 80 PVC' },
      { id: 'c', text: 'Materials previously used in oil and gas industry operations' },
      { id: 'd', text: 'Brass components' },
    ],
    correctAnswer: 'c',
    explanation: 'TAC 76.108 prohibits the use of materials previously used in oil and gas operations (used pipe, fittings, equipment) due to potential hydrocarbon contamination.',
    isMath: false,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q212',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'When must a well be disinfected after pump installation or repair?',
    choices: [
      { id: 'a', text: 'Only if the customer requests it' },
      { id: 'b', text: 'Only for wells deeper than 200 feet' },
      { id: 'c', text: 'When the well is used for human consumption' },
      { id: 'd', text: 'Disinfection is never required' },
    ],
    correctAnswer: 'c',
    explanation: 'Wells used for human consumption must be disinfected after any pump installation or repair work, using a chlorine solution with adequate contact time.',
    isMath: false,
    linkedQualIds: ['lead_install'],
  },

  // Submersible Motor & Control Box
  {
    id: 'q213',
    topic: 'electrical-safety',
    difficulty: 'intermediate',
    question: 'What is the maximum acceptable current unbalance for a three-phase submersible motor?',
    choices: [
      { id: 'a', text: '2%' },
      { id: 'b', text: '5%' },
      { id: 'c', text: '10%' },
      { id: 'd', text: '15%' },
    ],
    correctAnswer: 'b',
    explanation: 'Maximum acceptable current unbalance is 5%. A voltage unbalance of just 0.8% can cause greater than 5% current unbalance, leading to motor overheating and premature failure.',
    isMath: false,
    linkedQualIds: ['electrical', 'lead_diagnostic'],
  },
  {
    id: 'q214',
    topic: 'electrical-safety',
    difficulty: 'advanced',
    question: 'Where should a surge arrestor be grounded on a submersible pump installation?',
    choices: [
      { id: 'a', text: 'To the supply ground wire' },
      { id: 'b', text: 'To a driven ground rod near the panel' },
      { id: 'c', text: 'Metal-to-metal to the lowest drawdown water strata' },
      { id: 'd', text: 'To the neutral bus bar in the main panel' },
    ],
    correctAnswer: 'c',
    explanation: 'Surge arrestors must be grounded metal-to-metal to the lowest drawdown water strata — NOT to the supply ground or a driven ground rod. The ground wire must also be routed through the current-sensing device.',
    isMath: false,
    linkedQualIds: ['electrical', 'lead_install'],
  },
  {
    id: 'q215',
    topic: 'electrical-safety',
    difficulty: 'basic',
    question: 'What is the minimum insulation resistance for a new submersible motor?',
    choices: [
      { id: 'a', text: '0.5 megohms' },
      { id: 'b', text: '1 megohm' },
      { id: 'c', text: '2 megohms' },
      { id: 'd', text: '5 megohms' },
    ],
    correctAnswer: 'c',
    explanation: 'A new submersible motor should have a minimum of 2 megohms insulation resistance when tested with a megger.',
    isMath: false,
    linkedQualIds: ['electrical', 'lead_install'],
  },

  // Check Valves & Drop Pipe
  {
    id: 'q216',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'How close to the pump discharge must the first check valve be installed?',
    choices: [
      { id: 'a', text: 'Within 10 feet' },
      { id: 'b', text: 'Within 25 feet' },
      { id: 'c', text: 'Within 50 feet' },
      { id: 'd', text: 'Within 100 feet' },
    ],
    correctAnswer: 'b',
    explanation: 'The first check valve must be installed within 25 feet of the pump discharge to prevent backspin, upthrust, and water hammer when the pump cycles off.',
    isMath: false,
    linkedQualIds: ['lead_install'],
  },
  {
    id: 'q217',
    topic: 'pump-systems',
    difficulty: 'intermediate',
    question: 'What are the three main consequences of a failed check valve in a submersible pump system?',
    choices: [
      { id: 'a', text: 'Low pressure, high flow, and motor overheat' },
      { id: 'b', text: 'Backspin, upthrust, and water hammer' },
      { id: 'c', text: 'Cavitation, air lock, and dry running' },
      { id: 'd', text: 'Sand production, scale buildup, and corrosion' },
    ],
    correctAnswer: 'b',
    explanation: 'A failed check valve allows water to drain back through the pump, causing backspin (reverse impeller rotation), upthrust (upward force on shaft/bearings), and water hammer (pressure spikes from sudden flow changes).',
    isMath: false,
    linkedQualIds: ['lead_install', 'lead_diagnostic'],
  },

  // Pressure Tanks & CSV
  {
    id: 'q218',
    topic: 'pump-systems',
    difficulty: 'basic',
    question: 'What pre-charge pressure should a captive air (bladder) pressure tank be set to?',
    choices: [
      { id: 'a', text: '2 PSI above the cut-out pressure' },
      { id: 'b', text: 'Equal to the cut-in pressure' },
      { id: 'c', text: '2 PSI below the cut-in pressure' },
      { id: 'd', text: '10 PSI below the cut-out pressure' },
    ],
    correctAnswer: 'c',
    explanation: 'Captive air tank pre-charge should be set 2 PSI below the pressure switch cut-in pressure for optimal performance and tank life.',
    isMath: false,
    linkedQualIds: ['lead_install', 'lead_diagnostic'],
  },
  {
    id: 'q219',
    topic: 'diagnostics',
    difficulty: 'intermediate',
    question: 'What are the symptoms of a waterlogged pressure tank?',
    choices: [
      { id: 'a', text: 'Pump runs continuously with no pressure' },
      { id: 'b', text: 'Fast cycling (pump turns on/off every few seconds) and rapid pressure drop when pump is off' },
      { id: 'c', text: 'Water temperature fluctuations' },
      { id: 'd', text: 'High pressure with no water flow' },
    ],
    correctAnswer: 'b',
    explanation: 'A waterlogged tank has lost its air charge, resulting in fast cycling (short-cycling the pump every few seconds) and rapid pressure drop during pump off-time because there is no air cushion.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },

  // Groundwater Chemistry
  {
    id: 'q220',
    topic: 'water-quality',
    difficulty: 'intermediate',
    question: 'What is the relationship between specific electrical conductance (EC) and total dissolved solids (TDS)?',
    choices: [
      { id: 'a', text: 'They are unrelated measurements' },
      { id: 'b', text: 'TDS = EC × 2.0 to 3.0' },
      { id: 'c', text: 'TDS ≈ EC × 0.55 to 0.75' },
      { id: 'd', text: 'EC = TDS × 10' },
    ],
    correctAnswer: 'c',
    explanation: 'TDS can be estimated from electrical conductance using the factor TDS ≈ EC × 0.55 to 0.75, depending on mineral composition. EC is measured in µS/cm and TDS in mg/L.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q221',
    topic: 'water-quality',
    difficulty: 'advanced',
    question: 'Why is rapid analysis important for groundwater samples?',
    choices: [
      { id: 'a', text: 'Lab equipment degrades if samples wait too long' },
      { id: 'b', text: 'Dissolved gases escape, iron oxidizes, and biological activity alters concentrations after collection' },
      { id: 'c', text: 'The bottle material absorbs minerals over time' },
      { id: 'd', text: 'Rapid analysis is only needed for bacteriological samples' },
    ],
    correctAnswer: 'b',
    explanation: 'Water chemistry changes after collection: dissolved gases (CO2, H2S) escape changing pH, iron oxidizes from clear to particulate, biological activity alters nutrients, and temperature changes affect dissolved gas concentrations.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q222',
    topic: 'water-quality',
    difficulty: 'intermediate',
    question: 'How can you distinguish between a well problem and an aquifer condition when a customer reports water quality issues?',
    choices: [
      { id: 'a', text: 'Well problems always produce cloudy water; aquifer issues produce clear water' },
      { id: 'b', text: 'Sudden changes typically indicate a well problem; gradual trends indicate aquifer conditions' },
      { id: 'c', text: 'Only a lab can tell the difference' },
      { id: 'd', text: 'Aquifer issues only affect deep wells' },
    ],
    correctAnswer: 'b',
    explanation: 'History of previous water quality data helps differentiate: sudden changes (casing failure, screen deterioration, cross-contamination) are likely well problems, while gradual trends are likely aquifer conditions.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },

  // Texas Aquifers
  {
    id: 'q223',
    topic: 'groundwater-districts',
    difficulty: 'basic',
    question: 'How many major aquifers does Texas have?',
    choices: [
      { id: 'a', text: '5' },
      { id: 'b', text: '7' },
      { id: 'c', text: '9' },
      { id: 'd', text: '12' },
    ],
    correctAnswer: 'c',
    explanation: 'Texas has 9 major aquifers: Ogallala, Gulf Coast, Edwards (BFZ), Edwards-Trinity Plateau, Carrizo-Wilcox, Trinity, Pecos Valley, Seymour, and Hueco-Mesilla Bolsons.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },
  {
    id: 'q224',
    topic: 'groundwater-districts',
    difficulty: 'intermediate',
    question: 'What is the key difference between a confined and an unconfined aquifer?',
    choices: [
      { id: 'a', text: 'Confined aquifers have higher TDS' },
      { id: 'b', text: 'Confined aquifers are bounded by impermeable layers and water is under pressure; unconfined aquifers have a water table open to atmospheric pressure' },
      { id: 'c', text: 'Unconfined aquifers are always deeper' },
      { id: 'd', text: 'There is no practical difference for pump installers' },
    ],
    correctAnswer: 'b',
    explanation: 'Confined (artesian) aquifers are bounded above and below by impermeable layers with water under pressure that may rise above the formation top. Unconfined (water table) aquifers have their upper surface open to atmospheric pressure.',
    isMath: false,
    linkedQualIds: ['lead_diagnostic'],
  },

  // OSHA Sling Safety
  {
    id: 'q225',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'At a 30° sling angle from horizontal, what percentage of the load does each sling leg carry?',
    choices: [
      { id: 'a', text: '50%' },
      { id: 'b', text: '71%' },
      { id: 'c', text: '100%' },
      { id: 'd', text: '115%' },
    ],
    correctAnswer: 'c',
    explanation: 'At 30° from horizontal, each sling leg carries 100% of the load. This is why slings should never be used at angles less than 30° from horizontal. At 90° (vertical), each leg carries only 50%.',
    isMath: true,
    linkedQualIds: ['hoist', 'bighoss'],
  },
  {
    id: 'q226',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What is the maximum temperature for nylon web slings?',
    choices: [
      { id: 'a', text: '100°F' },
      { id: 'b', text: '150°F' },
      { id: 'c', text: '180°F' },
      { id: 'd', text: '250°F' },
    ],
    correctAnswer: 'c',
    explanation: 'Per OSHA 1910.184, nylon web slings should not be used above 180°F or below 0°F. Polyester web slings also have a 180°F upper limit.',
    isMath: false,
    linkedQualIds: ['hoist', 'bighoss'],
  },

  // OSHA Derrick & Crane
  {
    id: 'q227',
    topic: 'safety-osha',
    difficulty: 'basic',
    question: 'What is the single most important rule for derrick/crane load handling per OSHA 1910.181?',
    choices: [
      { id: 'a', text: 'Always use tag lines' },
      { id: 'b', text: 'Never exceed rated capacity' },
      { id: 'c', text: 'Keep boom angle above 45°' },
      { id: 'd', text: 'Use steel slings only' },
    ],
    correctAnswer: 'b',
    explanation: 'Never exceeding the rated capacity is the single most important load handling rule. Additionally, a minimum of two full wraps of rope must be maintained on the drum at all times.',
    isMath: false,
    linkedQualIds: ['hoist', 'bighoss'],
  },
  {
    id: 'q228',
    topic: 'safety-osha',
    difficulty: 'intermediate',
    question: 'How many minimum wraps of rope must be maintained on a derrick drum at all times?',
    choices: [
      { id: 'a', text: '1 wrap' },
      { id: 'b', text: '2 wraps' },
      { id: 'c', text: '3 wraps' },
      { id: 'd', text: '5 wraps' },
    ],
    correctAnswer: 'b',
    explanation: 'Per OSHA 1910.181, a minimum of 2 full wraps of rope must be maintained on the drum at all times during derrick operations.',
    isMath: false,
    linkedQualIds: ['hoist', 'bighoss'],
  },

  // TDLR Licensing
  {
    id: 'q229',
    topic: 'tdlr-licensing',
    difficulty: 'basic',
    question: 'What is the minimum passing score on a TDLR licensing examination?',
    choices: [
      { id: 'a', text: '60%' },
      { id: 'b', text: '70%' },
      { id: 'c', text: '75%' },
      { id: 'd', text: '80%' },
    ],
    correctAnswer: 'b',
    explanation: 'Per TAC 76.23, the minimum passing score on a TDLR licensing examination is 70%.',
    isMath: false,
    linkedQualIds: ['lead_install', 'lead_diagnostic'],
  },
  {
    id: 'q230',
    topic: 'tdlr-licensing',
    difficulty: 'intermediate',
    question: 'How many continuing education hours are required for TDLR license renewal, and how many must cover rules?',
    choices: [
      { id: 'a', text: '2 hours total, 1 hour rules' },
      { id: 'b', text: '4 hours total, 1 hour rules' },
      { id: 'c', text: '8 hours total, 2 hours rules' },
      { id: 'd', text: '6 hours total, 2 hours rules' },
    ],
    correctAnswer: 'b',
    explanation: 'Per TAC 76.24 and 76.25, TDLR license renewal requires 4 hours of continuing education, including a minimum of 1 hour covering rules and regulations.',
    isMath: false,
    linkedQualIds: ['lead_install', 'lead_diagnostic'],
  },
];
